/* eslint-disable */
// src/layouts/visitMachine/index.js

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import VisitMachineService, {
  normalizeCollectionPreviousValueRows,
} from "services/visitMachineService";
import LocalMachineService from "services/localMachineService";
import LocalMachineProfitPercentageService from "services/localMachineProfitPercentageService";
import VisitTransactionService from "services/visitTransactionService";
import { getVisitTransactionSaveErrorInfo } from "services/visitTransactionService";
import VisitMachineCounterVerificationService from "services/visitMachineCounterVerificationService";

import {
  Grid,
  Card,
  Box,
  Button,
  TextField,
  FormLabel,
  Alert,
  Chip,
  GlobalStyles,
} from "@mui/material";
import AppSnackbar from "components/common/AppSnackbar";
import AppActionDialog from "components/common/AppActionDialog";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { getCollectionStrategy } from "layouts/visitMachine/strategies/strategySelector";
import {
  buildDraftMachineList,
  getLocalMachinePresentationDetails,
  buildCollectionPreviousValuesRequestKey,
  getNextCollectionPreviousValuesRequestState,
  buildDraftInputHydrationMachines,
  buildCollectionUserInputsFromMachines,
  buildCollectionPayload,
  buildCompletedEditMachineListFromPersistedBaseline,
  canCompleteDraftProgress,
  canOpenCompletedEditMachine,
  buildCompletedEditWasCollectedMap,
  getDraftDateEditState,
  getDraftPersistedMachineCount,
  getCompletedEditMachineContextDateInput,
  getCompletedEditExitState,
  getDraftMachineStatusLabel,
  getDraftMachineDefaultWasCollected,
  getDraftPartialTotals,
  getCollectionGroupSubtotal,
  groupCollectionMachinesLikeLocalMachineIndex,
  getNextPendingLocalMachineId,
  getCompletedEditDateState,
  getCompletedEditMachineStatusLabel,
  getCompletedEditPersistedTotals,
  getCompletedEditPersistedBaseline,
  getEffectiveProfitPercentagesForMachine,
  getHistoricalProfitPercentageForEditMachine,
  hasCompleteHistoricalProfitPercentage,
  hasIncompleteIndividualSlotEntryExit,
  normalizeCreationDraftEditorMachine,
  normalizeCompletedEditMachine,
  normalizeCompletedEditDetailMachine,
  normalizeDraftMachineState,
  replaceEditedVisitMachine,
  mergeCollectionLocalSources,
  mergeDraftHydratedInputs,
  mergeDraftHydratedWasCollected,
  resolveCollectionHeaderData,
  resolveVisitMachineId,
  resolveCreationDraftWasCollected,
  shouldInitializeCompletedEditBaseline,
  validateCollectionBeforeSave,
} from "layouts/visitMachine/collectionWorkflow";

import useVisitBootstrap from "hooks/useVisitBootstrap";

import SaveBar from "components/common/SaveBar";
import {
  dangerOutlinedButtonSx,
  secondaryOutlinedButtonSx,
  successContainedButtonSx,
} from "components/common/buttonStyles";
import MachinesTableGroup from "components/visit/MachinesTableGroup";
import useNotify from "hooks/useNotify";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { hasRent } from "utils/rent";
import { hasMachineOperatorFormula } from "utils/machineFormula";
import {
  getDraftPersistedMoneyCollected,
} from "utils/collectionMachinePresentation";
import {
  getCollectionGeneralListPresentation,
  isNegativeCollectionAmount,
} from "utils/collectionGeneralListPresentation";
import {
  clearVisitDraftForTerminalResult,
  getVisitDraftTerminalDestination,
  rememberVisitDraft,
} from "utils/visitDraftSession";
import {
  clearCollectionDraftInputBuffer,
  clearCollectionDraftInputBufferMachine,
  getCollectionDraftInputBufferUserKey,
  getCollectionDraftInputBufferDirtyMachineIds,
  readCollectionDraftInputBuffer,
  restoreCollectionDraftInputBuffer,
  updateCollectionDraftInputBufferField,
  writeCollectionDraftInputBuffer,
} from "utils/collectionDraftInputBuffer";
import {
  clearCollectionEditInputBuffer,
  clearCollectionEditInputBufferMachine,
  getCollectionEditInputBufferDirtyMachineIds,
  readCollectionEditInputBuffer,
  restoreCollectionEditInputBuffer,
  updateCollectionEditInputBufferField,
  writeCollectionEditInputBuffer,
} from "utils/collectionEditInputBuffer";

// etiquetas (plural/singular + genero) y ti­tulo dinamico de alquiladas
import { machineTypeGender, machineTypeLabel, rentedGroupTitle } from "utils/machineLabels";

// === formatters dinamicos (mostrar decimales reales sin redondear)
import { nfRoundedDynamic as nfDyn, moneyFmtRoundedDynamic as moneyDyn } from "utils/formatters";
import { sortLocalMachinesFixed, sortTypeEntries } from "utils/localMachineOrder";
import {
  isPendingInitialValuesNoticeMachine,
  isInsertedInCurrentVisitNoticeMachine,
  isInsertedInCurrentVisitNoticeRow,
  isPersistedCollectedMachine,
  isVisitMachineCollected,
} from "utils/visitCollectionState";

import {
  TypeBlocksGlobalStyles,
  TypeBlock,
  TypeChipsHeader,
} from "ui/skins/typeBlocks";

const COLLECTION_NULL_RESULT = {
  actualCreditsIn: null,
  actualCreditsOut: null,
  netCreditsIn: null,
  netCreditsOut: null,
  creditsCollected: null,
  moneyIn: null,
  moneyOut: null,
  netMoneyIn: null,
  netMoneyOut: null,
  moneyCollected: null,
  clientAmount: null,
  ownerAmount: null,
  collectedStyle: {},
  HasReset: false,
  PostResetStartIn: null,
  PostResetStartOut: null,
};

const COLLECTION_EDIT_HISTORICAL_ERROR_CODES = new Set([
  "COLLECTION_EDIT_DATE_UNSAFE_HISTORICAL_CONTEXT",
  "COLLECTION_EDIT_DATE_CROSSES_LOCAL_MACHINE_CHANGE",
  "COLLECTION_EDIT_DATE_CROSSES_PROFIT_PERCENTAGE_CHANGE",
  "COLLECTION_EDIT_DATE_COUNTER_CONTEXT_CHANGED",
  "COLLECTION_EDIT_DATE_DUPLICATED_FOR_LOCAL",
  "COLLECTION_EDIT_DATE_BEFORE_PREVIOUS_COLLECTION",
]);

const collectionGeneralTypographyStyles = {
  "#visit-edit .collection-page-header .MuiTypography-h6": { fontSize: "1.2rem" },
  "#visit-edit .collection-page-header .MuiTypography-button": { fontSize: "1.05rem" },
  "#visit-edit .collection-page-header .MuiButton-root": {
    fontSize: "0.9rem",
    padding: "0.47rem 1.25rem",
  },
  "#visit-edit .collection-date-label": { fontSize: "1.26rem" },
  "#visit-edit .collection-date-panel .MuiTypography-caption": { fontSize: "0.9rem" },
  "#visit-edit .collection-date-panel .MuiInputBase-input": { fontSize: "1.05rem" },
  "#visit-edit .collection-date-panel .MuiFormHelperText-root": { fontSize: "0.9rem" },
  "#visit-edit .collection-date-panel .MuiButton-root": {
    fontSize: "0.9rem",
    padding: "0.45rem 1.1rem",
    minHeight: 38,
  },
  "#visit-edit .collection-context-text": { fontSize: "1.08rem" },
  "#visit-edit .collection-context-alert .MuiAlert-message": { fontSize: "1.05rem" },
  "#visit-edit .collection-context-alert .MuiAlert-icon": { fontSize: "1.45rem" },
  "#visit-edit .collection-context-surface .MuiTypography-caption": { fontSize: "0.9rem" },
  "#visit-edit .collection-context-surface .MuiButton-root": {
    fontSize: "0.9rem",
    padding: "0.45rem 1.1rem",
  },
  "#visit-edit .collection-general-list .collection-machine-name": { fontSize: "1.26rem" },
  "#visit-edit .collection-general-list .collection-machine-secondary": {
    fontSize: "1.08rem",
    color: "#000000",
  },
  "#visit-edit .collection-general-list .MuiChip-root": { minHeight: 35 },
  "#visit-edit .collection-general-list .collection-machine-chips": { gap: "0.6rem" },
  "#visit-edit .collection-general-list .MuiChip-label": {
    fontSize: "1.17rem",
    paddingLeft: "0.9rem",
    paddingRight: "0.9rem",
  },
  "#visit-edit .collection-general-list .collection-open-button": {
    fontSize: "1.08rem",
    padding: "0.56rem 1.5rem",
    minHeight: 46,
  },
  "#visit-edit .collection-general-list .collection-totals-block .MuiTypography-button": {
    fontSize: "1.26rem",
  },
  "#visit-edit .collection-general-list .collection-totals-block .MuiTypography-caption": {
    fontSize: "1.08rem",
  },
  "#visit-edit .collection-general-list .collection-group-subtotal .collection-group-subtotal-line": {
    fontSize: "20px",
  },
  "#visit-edit .collection-general-list .collection-draft-footer-button": {
    fontSize: "1.08rem",
    padding: "0.56rem 1.5rem",
    minHeight: 46,
  },
  "#visit-edit .collection-general-list .vtb": { padding: "0.75rem 0.875rem 0.875rem" },
  "#visit-edit .collection-general-list .vtb-inner": { padding: "0.625rem 0.625rem 0.75rem" },
  "#visit-edit .collection-general-list .vtb > .MuiBox-root:first-child > span": {
    fontSize: "1.35rem !important",
    padding: "0.32rem 0.94rem",
  },
  "@media (max-width: 375px)": {
    "#visit-edit .collection-page-header": {
      alignItems: "flex-start !important",
      flexDirection: "column",
      gap: "0.65rem",
    },
    "#visit-edit .collection-page-header > *": { width: "100%", textAlign: "left" },
    "#visit-edit .collection-page-header .MuiButton-root": { width: "100%" },
    "#visit-edit .collection-date-panel > *": { minWidth: "0 !important", width: "100%" },
    "#visit-edit .collection-date-panel .MuiButton-root": { flex: "1 1 100%" },
    "#visit-edit .collection-general-list .collection-machine-card": {
      alignItems: "stretch",
      gap: "0.75rem",
    },
    "#visit-edit .collection-general-list .collection-machine-card > .MuiBox-root:first-of-type": {
      minWidth: "0 !important",
      width: "100%",
    },
    "#visit-edit .collection-general-list .collection-machine-card > .MuiButton-root": {
      width: "100%",
    },
    "#visit-edit .collection-general-list .collection-draft-footer-actions": {
      width: "100%",
    },
    "#visit-edit .collection-general-list .collection-draft-footer-button": {
      flex: "1 1 100%",
    },
  },
  "@media (max-width: 600px)": {
    "#visit-edit .individual-editor-toolbar": { alignItems: "stretch" },
    "#visit-edit .individual-editor-toolbar > .MuiButton-root": { width: "100%" },
    "#visit-edit .individual-editor-save-actions": {
      display: "grid !important",
      gridTemplateColumns: "minmax(0, 1fr)",
      width: "100%",
    },
    "#visit-edit .individual-editor-save-actions .MuiButton-root": {
      width: "100%",
      whiteSpace: "normal",
    },
    "#visit-edit .collection-edit-footer-actions": { width: "100%" },
    "#visit-edit .collection-edit-footer-actions .MuiButton-root": {
      flex: "1 1 100%",
      whiteSpace: "normal",
    },
  },
};

/* File: src/layouts/visitMachines/index.js (VisitMachine)

Página de crear/editar recaudación.
*/

function VisitMachine() {
  const BUSINESS_TIME_ZONE = "America/Montevideo";
  const isPresent = (value) => value !== undefined && value !== null && value !== "";
  const pickFirstPresent = (...values) => values.find(isPresent);
  const pickNumber = (source, ...keys) => {
    for (const key of keys) {
      const value = source?.[key];
      if (!isPresent(value)) continue;
      const number = Number(value);
      if (Number.isFinite(number)) return number;
    }
    return undefined;
  };
  const synthesizeHistoricalPreviousValues = useCallback((machine = {}) => {
    const inferPrevious = (explicitKeys, actualKeys, netKeys) => {
      const explicit = pickNumber(machine, ...explicitKeys);
      if (explicit !== undefined) return explicit;

      const actual = pickNumber(machine, ...actualKeys);
      const net = pickNumber(machine, ...netKeys);
      if (actual === undefined || net === undefined) return undefined;
      return actual - net;
    };

    const prevCreditsIn = inferPrevious(
      ["PrevCreditsIn", "prevCreditsIn", "LastCreditsIn", "lastCreditsIn"],
      ["actualCreditsIn", "CreditsIn"],
      ["netCreditsIn", "NetCreditsIn"]
    );
    const prevCreditsOut = inferPrevious(
      ["PrevCreditsOut", "prevCreditsOut", "LastCreditsOut", "lastCreditsOut"],
      ["actualCreditsOut", "CreditsOut"],
      ["netCreditsOut", "NetCreditsOut"]
    );
    const prevMoneyIn = inferPrevious(
      ["PrevMoneyIn", "prevMoneyIn", "LastMoneyIn", "lastMoneyIn"],
      ["moneyIn", "MoneyIn"],
      ["netMoneyIn", "NetMoneyIn"]
    );
    const prevMoneyOut = inferPrevious(
      ["PrevMoneyOut", "prevMoneyOut", "LastMoneyOut", "lastMoneyOut"],
      ["moneyOut", "MoneyOut"],
      ["netMoneyOut", "NetMoneyOut"]
    );

    return {
      ...machine,
      ...(prevCreditsIn !== undefined
        ? { PrevCreditsIn: prevCreditsIn, lastCreditsIn: prevCreditsIn }
        : {}),
      ...(prevCreditsOut !== undefined
        ? { PrevCreditsOut: prevCreditsOut, lastCreditsOut: prevCreditsOut }
        : {}),
      ...(prevMoneyIn !== undefined
        ? { PrevMoneyIn: prevMoneyIn, lastMoneyIn: prevMoneyIn }
        : {}),
      ...(prevMoneyOut !== undefined
        ? { PrevMoneyOut: prevMoneyOut, lastMoneyOut: prevMoneyOut }
        : {}),
    };
  }, []);
  const toBusinessDateKey = useCallback((dateLike) => {
    if (!dateLike) return null;
    const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
    if (Number.isNaN(date.getTime())) return null;

    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: BUSINESS_TIME_ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(date);

    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;

    if (!year || !month || !day) return null;
    return `${year}-${month}-${day}`;
  }, []);

  const mergeMachineWithVisitDetail = useCallback((machine, visitDetail) => {
    if (!visitDetail) return machine;

    const mergedMachineType =
      (visitDetail?.MachineType && (
        isPresent(visitDetail.MachineType?.Type) ||
        isPresent(visitDetail.MachineType?.Id)
      ))
        ? {
            ...machine?.MachineType,
            ...visitDetail.MachineType,
            Type: pickFirstPresent(visitDetail.MachineType?.Type, machine?.MachineType?.Type),
            Id: pickFirstPresent(
              visitDetail.MachineType?.Id,
              visitDetail?.MachineTypeId,
              machine?.MachineType?.Id,
              machine?.MachineTypeId
            ),
          }
        : (machine?.MachineType || null);

    const mergedMachineInfo =
      (visitDetail?.Machine && (
        isPresent(visitDetail.Machine?.Name) ||
        isPresent(visitDetail.Machine?.SerialNumber) ||
        isPresent(visitDetail.Machine?.LocalDisplayNumber) ||
        isPresent(visitDetail.Machine?.Id)
      ))
        ? {
            ...machine?.Machine,
            ...visitDetail.Machine,
            Name: pickFirstPresent(
              visitDetail.Machine?.Name,
              visitDetail?.MachineName,
              machine?.Machine?.Name,
              machine?.MachineName
            ),
            SerialNumber: pickFirstPresent(
              visitDetail.Machine?.SerialNumber,
              visitDetail?.SerialNumber,
              machine?.Machine?.SerialNumber,
              machine?.SerialNumber
            ),
            LocalDisplayNumber: pickFirstPresent(
              visitDetail.Machine?.LocalDisplayNumber,
              visitDetail?.LocalDisplayNumber,
              machine?.Machine?.LocalDisplayNumber,
              machine?.LocalDisplayNumber
            ),
            Id: pickFirstPresent(visitDetail.Machine?.Id, machine?.Machine?.Id, machine?.MachineId),
          }
        : (machine?.Machine || null);

    const mergedFormula =
      (visitDetail?.Formula && (
        isPresent(visitDetail.Formula?.Type) ||
        isPresent(visitDetail.Formula?.Id) ||
        isPresent(visitDetail.Formula?.Name)
      ))
        ? {
            ...machine?.Formula,
            ...visitDetail.Formula,
            Id: pickFirstPresent(
              visitDetail.Formula?.Id,
              visitDetail?.FormulaId,
              machine?.Formula?.Id,
              machine?.FormulaId
            ),
            Type: pickFirstPresent(
              visitDetail.Formula?.Type,
              visitDetail?.FormulaType,
              machine?.Formula?.Type,
              machine?.FormulaType
            ),
            Name: pickFirstPresent(
              visitDetail.Formula?.Name,
              visitDetail?.FormulaName,
              machine?.Formula?.Name,
              machine?.FormulaName
            ),
            Value: pickFirstPresent(
              visitDetail.Formula?.Value,
              visitDetail?.FormulaValue,
              machine?.Formula?.Value,
              machine?.FormulaValue
            ),
          }
        : (machine?.Formula || null);

    return {
      ...machine,
      ...visitDetail,
      LocalMachineId: pickFirstPresent(
        visitDetail?.LocalMachineId,
        visitDetail?.Id,
        machine?.LocalMachineId,
        machine?.Id
      ),
      MachineType: mergedMachineType,
      MachineTypeId: pickFirstPresent(
        visitDetail?.MachineTypeId,
        visitDetail?.MachineType?.Id,
        machine?.MachineTypeId,
        machine?.MachineType?.Id
      ),
      Machine: mergedMachineInfo,
      MachineName: pickFirstPresent(
        visitDetail?.MachineName,
        visitDetail?.Machine?.Name,
        machine?.MachineName,
        machine?.Machine?.Name
      ),
      SerialNumber: pickFirstPresent(
        visitDetail?.SerialNumber,
        visitDetail?.Machine?.SerialNumber,
        machine?.SerialNumber,
        machine?.Machine?.SerialNumber
      ),
      LocalDisplayNumber: pickFirstPresent(
        visitDetail?.LocalDisplayNumber,
        visitDetail?.Machine?.LocalDisplayNumber,
        machine?.LocalDisplayNumber,
        machine?.Machine?.LocalDisplayNumber
      ),
      Formula: mergedFormula,
      FormulaId: pickFirstPresent(
        visitDetail?.FormulaId,
        visitDetail?.Formula?.Id,
        machine?.FormulaId,
        machine?.Formula?.Id
      ),
      FormulaType: pickFirstPresent(
        visitDetail?.FormulaType,
        visitDetail?.Formula?.Type,
        machine?.FormulaType,
        machine?.Formula?.Type
      ),
      FormulaName: pickFirstPresent(
        visitDetail?.FormulaName,
        visitDetail?.Formula?.Name,
        machine?.FormulaName,
        machine?.Formula?.Name
      ),
      FormulaValue: pickFirstPresent(
        visitDetail?.FormulaValue,
        visitDetail?.Formula?.Value,
        machine?.FormulaValue,
        machine?.Formula?.Value
      ),
      IsAccumulative: pickFirstPresent(
        visitDetail?.IsAccumulative,
        machine?.IsAccumulative
      ),
      LocalMachineProfitPercentageId: pickFirstPresent(
        visitDetail?.LocalMachineProfitPercentageId,
        machine?.LocalMachineProfitPercentageId
      ),
      actualCreditsIn: pickNumber(
        visitDetail,
        "actualCreditsIn",
        "CreditsIn"
      ) ?? pickNumber(machine, "actualCreditsIn", "CreditsIn"),
      actualCreditsOut: pickNumber(
        visitDetail,
        "actualCreditsOut",
        "CreditsOut"
      ) ?? pickNumber(machine, "actualCreditsOut", "CreditsOut"),
      netCreditsIn: pickNumber(
        visitDetail,
        "netCreditsIn",
        "NetCreditsIn"
      ) ?? pickNumber(machine, "netCreditsIn", "NetCreditsIn"),
      netCreditsOut: pickNumber(
        visitDetail,
        "netCreditsOut",
        "NetCreditsOut"
      ) ?? pickNumber(machine, "netCreditsOut", "NetCreditsOut"),
      moneyIn: pickNumber(visitDetail, "moneyIn", "MoneyIn")
        ?? pickNumber(machine, "moneyIn", "MoneyIn"),
      moneyOut: pickNumber(visitDetail, "moneyOut", "MoneyOut")
        ?? pickNumber(machine, "moneyOut", "MoneyOut"),
      netMoneyIn: pickNumber(visitDetail, "netMoneyIn", "NetMoneyIn")
        ?? pickNumber(machine, "netMoneyIn", "NetMoneyIn"),
      netMoneyOut: pickNumber(visitDetail, "netMoneyOut", "NetMoneyOut")
        ?? pickNumber(machine, "netMoneyOut", "NetMoneyOut"),
      creditsCollected: pickNumber(
        visitDetail,
        "creditsCollected",
        "CreditsCollected"
      ) ?? pickNumber(machine, "creditsCollected", "CreditsCollected"),
      moneyCollected: pickNumber(
        visitDetail,
        "moneyCollected",
        "MoneyCollected"
      ) ?? pickNumber(machine, "moneyCollected", "MoneyCollected"),
      clientAmount: pickNumber(visitDetail, "clientAmount", "ClientAmount")
        ?? pickNumber(machine, "clientAmount", "ClientAmount"),
      ownerAmount: pickNumber(visitDetail, "ownerAmount", "OwnerAmount")
        ?? pickNumber(machine, "ownerAmount", "OwnerAmount"),
      note: pickFirstPresent(
        visitDetail?.note,
        visitDetail?.Note,
        machine?.note,
        machine?.Note,
        ""
      ),
    };
  }, []);

  const resolveMachineKey = (machine) => Number(machine?.LocalMachineId ?? machine?.Id ?? 0);
  const buildHistoricalEditLocalMachineRows = useCallback((currentRows = [], historicalRows = []) => {
    const normalizedCurrentRows = Array.isArray(currentRows) ? currentRows : [];
    const normalizedHistoricalRows = Array.isArray(historicalRows) ? historicalRows : [];

    if (!normalizedHistoricalRows.length) {
      return normalizedCurrentRows;
    }

    return normalizedHistoricalRows.map((historicalRow) => {
      const localMachineId = resolveMachineKey(historicalRow);
      const currentRow =
        normalizedCurrentRows.find((machine) => resolveMachineKey(machine) === localMachineId) || {
          Id: localMachineId,
          LocalMachineId: localMachineId,
        };

      return mergeMachineWithVisitDetail(currentRow, historicalRow);
    });
  }, [mergeMachineWithVisitDetail]);

  const toDateInputValue = (dateLike) => {
    const businessDateKey = toBusinessDateKey(dateLike || new Date());
    if (businessDateKey) return businessDateKey;

    const fallback = new Date();
    return `${fallback.getFullYear()}-${String(fallback.getMonth() + 1).padStart(2, "0")}-${String(
      fallback.getDate()
    ).padStart(2, "0")}`;
  };
  const formatDateDDMMYYYY = (dateLike) => {
    if (!dateLike) return "-";
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "-";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const formatDateKeyDDMMYYYY = (dateKey) => {
    if (!dateKey || !/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey))) {
      return formatDateDDMMYYYY(dateKey);
    }
    const [year, month, day] = String(dateKey).split("-");
    return `${day}/${month}/${year}`;
  };

  const buildVisitDateTime = (dateInput, baseDateTime) => {
    if (!dateInput) return null;
    const [y, m, d] = String(dateInput)
      .split("-")
      .map((x) => Number(x));
    if (!y || !m || !d) return null;

    const base = baseDateTime ? new Date(baseDateTime) : new Date();
    const safeBase = Number.isNaN(base.getTime()) ? new Date() : base;
    safeBase.setFullYear(y, m - 1, d);
    return safeBase.toISOString();
  };

  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("lg"));
  const { state } = useLocation();
  const { id, visitId } = useParams();
  const navigate = useNavigate();
  const handleBackToLocal = () => {
    const targetLocalId = local?.Id ?? state?.localId ?? id;
    if (!targetLocalId) {
      navigate("/local");
      return;
    }
    navigate(`/local/${targetLocalId}`, { state: { local } });
  };
  const navigateToCollectionDetail = () => {
    const detailLocalId = Number(local?.Id ?? state?.localId ?? id);
    const detailVisitId = Number(visitWithCollectionId ?? visitId);
    const detailPath =
      Number.isFinite(detailLocalId) && detailLocalId > 0 && Number.isFinite(detailVisitId) && detailVisitId > 0
        ? `/local/${detailLocalId}/recaudaciones/${detailVisitId}`
        : `/visit-details/${visitWithCollectionId ?? visitId}`;
    navigate(detailPath, {
      replace: true,
      state: { local, returnTo: state?.returnTo || null },
    });
  };

  const isEditMode = Boolean(visitId);
  const { notifySuccess, notifyError } = useNotify();
  const [saving, setSaving] = useState(false);
  const [draftProgress, setDraftProgress] = useState(null);
  const [isLoadingDraftProgress, setIsLoadingDraftProgress] = useState(false);
  const [selectedDraftLocalMachineId, setSelectedDraftLocalMachineId] = useState(null);
  const [selectedEditVisitMachineId, setSelectedEditVisitMachineId] = useState(null);
  const [editMachineRows, setEditMachineRows] = useState([]);
  const [editTotals, setEditTotals] = useState(null);
  const [editSuccessMessage, setEditSuccessMessage] = useState("");
  const [editSuccessMessageKey, setEditSuccessMessageKey] = useState(0);
  const editMachineSavingRef = useRef(false);
  const editDateSavingRef = useRef(false);
  const initializedCompletedEditVisitIdRef = useRef(null);
  const lastEffectiveEditVisitIdRef = useRef(null);
  const [isSavingEditDate, setIsSavingEditDate] = useState(false);
  const [openCancelDraftDialog, setOpenCancelDraftDialog] = useState(false);
  const [openIncompleteSlotDialog, setOpenIncompleteSlotDialog] = useState(false);
  const [openDraftDateResetDialog, setOpenDraftDateResetDialog] = useState(false);
  const [isDraftDateChangeRequested, setIsDraftDateChangeRequested] = useState(false);
  const [isSavingDraftDate, setIsSavingDraftDate] = useState(false);
  const [draftAction, setDraftAction] = useState(null);
  const draftActionRef = useRef(null);
  const draftTerminalRef = useRef(false);
  const savingRef = useRef(false);
  const draftDateSavingRef = useRef(false);

  const [openBackDialog, setOpenBackDialog] = useState(false);
  const [openDiscardEditMachineDialog, setOpenDiscardEditMachineDialog] = useState(false);
  const [openPendingEditDialog, setOpenPendingEditDialog] = useState(false);
  const suppressDirtyRef = useRef(false);
  const handleCloseEditSuccess = (_event, reason) => {
    if (reason === "clickaway") return;
    setEditSuccessMessage("");
  };
  const isAnyEditOperationActive = () =>
    savingRef.current ||
    draftActionRef.current ||
    draftDateSavingRef.current ||
    editMachineSavingRef.current ||
    editDateSavingRef.current;

  const handleOpenBackDialog = () => {
    if (isAnyEditOperationActive()) return;
    const exitState = getCompletedEditExitState({
      isEditMode,
      isCollectionDirty,
      editDateChanged: editDateState?.changed === true,
    });
    if (exitState.canExitDirectly) {
      navigateToCollectionDetail();
      return;
    }
    setOpenBackDialog(true);
  };
  const handleCloseBackDialog = () => setOpenBackDialog(false);

  const handleFinishCompletedEdit = () => {
    if (isAnyEditOperationActive()) return;
    if (isCollectionDirty || editDateState.changed) {
      setOpenPendingEditDialog(true);
      return;
    }
    navigateToCollectionDetail();
  };

  const handleExitCompletedEdit = () => {
    if (isAnyEditOperationActive()) return;
    if (!isCollectionDirty && !editDateState.changed) {
      navigateToCollectionDetail();
      return;
    }
    setOpenBackDialog(true);
  };

  const handleConfirmBackToLocal = () => {
    if (isAnyEditOperationActive()) return;
    setOpenBackDialog(false);
    if (isEditMode) {
      clearEditInputBufferForCurrentVisit();
      navigateToCollectionDetail();
      return;
    }
    handleBackToLocal(); // cumple la promesa (y se pierden cambios)
  };

  const onlyActive = (arr) => (arr || []).filter(d => d.IsActive === true);

  const onlyActiveProfit = (arr) =>
    (arr || []).filter((pp) => pp.IsActive !== false);

  // - bootstrap edición/fallback
  const {
    isEdit: _isEdit,
    loading: bootLoading,
    error: bootError,
    local: bootLocal,
    profitPercentages: bootProfits,
    localMachineDetails: bootLM,
    calculatedVisitMachines: bootCalc,
    lastCredits: bootPrevCredits,
    visitWithCollectionId: bootVisitId,
    loadedVisitId: bootLoadedVisitId,
    visitWithCollection: bootVisitWithCollection,
    visitDateTime: bootVisitDateTime,
    lastLocalVisitBeforeId: bootLastLocalVisitBeforeId,
    lastLocalVisitBeforeDate: bootLastLocalVisitBeforeDate,
  } = useVisitBootstrap();

  const [creationDraftVisitId, setCreationDraftVisitId] = useState(
    state?.visitWithCollectionId ?? state?.bcVisitId ?? null
  );

  // - tomar el id del hook si es edición
  const visitWithCollectionId = isEditMode
  ? (visitId ?? bootVisitId)   // en edición, el id viene en la URL
  : creationDraftVisitId;

  const buildInputsFromMachines = useCallback(
    (machines = []) => buildCollectionUserInputsFromMachines(machines),
    []
  );

  // Si estás en edición, extrae los campos *que el usuario edita* por tipo y fórmula.
  const buildInitialInputs = () => {
    return buildInputsFromMachines(
      (state?.calculatedVisitMachines || []).map(normalizeCompletedEditDetailMachine)
    );
  };

  const [userInputs, setUserInputs] = useState(buildInitialInputs());
  const [localMachineDetails, setLocalMachineDetails] = useState(state?.localMachineDetails || []);
  const [localMachinePresentationDetails, setLocalMachinePresentationDetails] = useState(
    state?.localMachineDetails || []
  );
  const [profitPercentages, setProfitPercentages] = useState(state?.profitPercentages || []);
  const [visitMachineDetails, setVisitMachineDetails] = useState([]);
  const [counterVerificationContextsByLocalMachineId, setCounterVerificationContextsByLocalMachineId] = useState({});
  const [machineTypes, setMachineTypes] = useState([]);
  const [wasCollectedByLM, setWasCollectedByLM] = useState({});
  const dirtyWasCollectedByLMRef = useRef({});
  const dirtyDraftInputFieldsRef = useRef({});
  const userInputsRef = useRef(userInputs);
  const wasCollectedByLMRef = useRef(wasCollectedByLM);
  const previousValuesLoadingRef = useRef(null);
  const previousValuesRequestIdRef = useRef(0);
  const [draftInputBufferConflictCount, setDraftInputBufferConflictCount] = useState(0);
  const [draftInputDirtyMachineIds, setDraftInputDirtyMachineIds] = useState([]);
  const [editInputBufferConflictCount, setEditInputBufferConflictCount] = useState(0);
  const [editInputDirtyMachineIds, setEditInputDirtyMachineIds] = useState([]);
  const [settledInputFields, setSettledInputFields] = useState({});
  const inputSettleTimersRef = useRef({});
  const [visitDateInput, setVisitDateInput] = useState(() => toDateInputValue(state?.visitDateTime));
  const [editPersistedDateInput, setEditPersistedDateInput] = useState(() =>
    toDateInputValue(state?.visitDateTime)
  );
  const [collectionDateBounds, setCollectionDateBounds] = useState({
    MinSelectableCollectionDate: null,
    MaxSelectableCollectionDate: toDateInputValue(new Date()),
    ReferenceKind: "none",
    ReferenceDate: null,
  });
  const [isLoadingCollectionDateBounds, setIsLoadingCollectionDateBounds] = useState(false);
  const [collectionEditSafeBounds, setCollectionEditSafeBounds] = useState(null);
  const [isLoadingCollectionEditSafeBounds, setIsLoadingCollectionEditSafeBounds] =
    useState(false);
  const [collectionEditSafeBoundsError, setCollectionEditSafeBoundsError] = useState(null);
  const [historicalEditDateError, setHistoricalEditDateError] = useState(null);
  const [actionError, setActionError] = useState("");
  const [isCollectionDirty, setIsCollectionDirty] = useState(false);
  const [resetVersion, setResetVersion] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [lastLocalVisitId, setLastLocalVisitId] = useState(
    state?.LastLocalVisitId ?? state?.lastLocalVisitId ?? null
  );
  const [lastLocalVisitDate, setLastLocalVisitDate] = useState(
    state?.LastLocalVisitDate ?? state?.lastLocalVisitDate ?? null
  );
  const [lastLocalVisitBeforeId, setLastLocalVisitBeforeId] = useState(
    state?.LastLocalVisitBeforeId ??
      state?.lastLocalVisitBeforeId ??
      bootLastLocalVisitBeforeId ??
      null
  );
  const [lastLocalVisitBeforeDate, setLastLocalVisitBeforeDate] = useState(
    state?.LastLocalVisitBeforeDate ??
      state?.lastLocalVisitBeforeDate ??
      bootLastLocalVisitBeforeDate ??
      null
  );
  const [firstHistoricalMachineStart, setFirstHistoricalMachineStart] = useState(null);
  const [latestActiveMachineStart, setLatestActiveMachineStart] = useState(null);
  const [latestInactiveMachineFinishDate, setLatestInactiveMachineFinishDate] = useState(null);
  const local = useMemo(
    () =>
      mergeCollectionLocalSources({
        stateLocal: state?.local || null,
        bootLocal,
        expectedLocalId: state?.localId ?? id ?? bootLocal?.Id,
      }) ||
      bootLocal ||
      null,
    [state?.local, state?.localId, id, bootLocal]
  );
  const effectiveLocalId = useMemo(() => {
    const parsed = Number(local?.Id ?? state?.localId ?? id);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [local?.Id, state?.localId, id]);
  const effectiveEditVisitId = useMemo(() => {
    const parsed = Number(visitWithCollectionId ?? visitId);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [visitWithCollectionId, visitId]);
  const draftPersistedDateInput = useMemo(
    () =>
      toDateInputValue(
        draftProgress?.visit?.DateTime ||
          state?.visitDateTime ||
          bootVisitDateTime ||
          new Date()
      ),
    [draftProgress?.visit?.DateTime, state?.visitDateTime, bootVisitDateTime]
  );
  const machineContextDateInput = useMemo(
    () =>
      getCompletedEditMachineContextDateInput({
        isEditMode,
        persistedDate: editPersistedDateInput,
        candidateDate: isEditMode ? visitDateInput : draftPersistedDateInput,
      }),
    [isEditMode, editPersistedDateInput, visitDateInput, draftPersistedDateInput]
  );
  const effectiveVisitDateTimeForPreview = useMemo(
    () => buildVisitDateTime(machineContextDateInput, state?.visitDateTime || bootVisitDateTime || null),
    [machineContextDateInput, state?.visitDateTime, bootVisitDateTime]
  );

  useEffect(() => {
    userInputsRef.current = userInputs;
  }, [userInputs]);

  useEffect(() => {
    wasCollectedByLMRef.current = wasCollectedByLM;
  }, [wasCollectedByLM]);

  const getDraftInputBufferContext = useCallback(() => {
    if (isEditMode) return null;
    const userKey = getCollectionDraftInputBufferUserKey();
    const parsedVisitId = Number(visitWithCollectionId);
    const parsedLocalId = Number(effectiveLocalId);
    if (!userKey || !Number.isFinite(parsedVisitId) || parsedVisitId <= 0) return null;
    if (!Number.isFinite(parsedLocalId) || parsedLocalId <= 0) return null;
    return {
      userKey,
      visitId: parsedVisitId,
      localId: parsedLocalId,
      visitDateTime: draftProgress?.visit?.DateTime || null,
    };
  }, [isEditMode, visitWithCollectionId, effectiveLocalId, draftProgress?.visit?.DateTime]);

  const syncDraftInputDirtyStateFromBuffer = useCallback((buffer = null) => {
    const dirtyMachineIds = getCollectionDraftInputBufferDirtyMachineIds(buffer);
    const nextDirtyFields = {};
    Object.entries(buffer?.machines || {}).forEach(([localMachineId, machineBuffer]) => {
      Object.keys(machineBuffer?.fields || {}).forEach((field) => {
        nextDirtyFields[`${Number(localMachineId)}:${field}`] = true;
      });
    });
    dirtyDraftInputFieldsRef.current = nextDirtyFields;
    setDraftInputDirtyMachineIds(dirtyMachineIds);
    if (!isEditMode) setIsCollectionDirty(dirtyMachineIds.length > 0);
    return dirtyMachineIds;
  }, [isEditMode]);

  const getDraftFieldSnapshot = useCallback((localMachineId, field) => {
    const lmId = Number(localMachineId);
    const inputs = userInputsRef.current?.[lmId] ?? userInputsRef.current?.[String(lmId)] ?? {};
    if (field === "WasCollected") {
      if (typeof wasCollectedByLMRef.current?.[lmId] === "boolean") {
        return { exists: true, value: wasCollectedByLMRef.current[lmId] };
      }
      if (typeof wasCollectedByLMRef.current?.[String(lmId)] === "boolean") {
        return { exists: true, value: wasCollectedByLMRef.current[String(lmId)] };
      }
    }
    if (Object.prototype.hasOwnProperty.call(inputs || {}, field)) {
      return { exists: true, value: inputs[field] };
    }
    return { exists: false, value: undefined };
  }, []);

  const getDraftBaseVisitMachineId = useCallback((localMachineId) => {
    const lmId = Number(localMachineId);
    const expectedMachine = (draftProgress?.expectedMachines || []).find(
      (machine) => Number(machine?.LocalMachineId ?? machine?.Id) === lmId
    );
    const persisted = expectedMachine?.VisitMachine ||
      (draftProgress?.visitMachines || []).find((machine) => Number(machine?.LocalMachineId) === lmId) ||
      null;
    return persisted?.VisitMachineId ?? persisted?.LastVisitMachineId ?? persisted?.Id ?? null;
  }, [draftProgress?.expectedMachines, draftProgress?.visitMachines]);

  const markDraftInputFieldDirty = useCallback((localMachineId, field, localValue, baseSnapshot = null) => {
    const context = getDraftInputBufferContext();
    const lmId = Number(localMachineId);
    if (!context || !Number.isFinite(lmId) || lmId <= 0) return;
    const fieldKey = `${lmId}:${field}`;
    const snapshot = baseSnapshot || getDraftFieldSnapshot(lmId, field);
    dirtyDraftInputFieldsRef.current = {
      ...dirtyDraftInputFieldsRef.current,
      [fieldKey]: true,
    };
    updateCollectionDraftInputBufferField({
      ...context,
      localMachineId: lmId,
      field,
      baseValue: snapshot.value,
      baseExists: snapshot.exists,
      localValue,
      baseVisitMachineId: getDraftBaseVisitMachineId(lmId),
    });
    const readBuffer = readCollectionDraftInputBuffer(context);
    syncDraftInputDirtyStateFromBuffer(readBuffer.buffer);
  }, [
    getDraftInputBufferContext,
    getDraftFieldSnapshot,
    getDraftBaseVisitMachineId,
    syncDraftInputDirtyStateFromBuffer,
  ]);

  const clearDraftInputBufferForCurrentVisit = useCallback(() => {
    const context = getDraftInputBufferContext();
    if (!context) return;
    clearCollectionDraftInputBuffer(context);
    syncDraftInputDirtyStateFromBuffer(null);
    setDraftInputBufferConflictCount(0);
  }, [getDraftInputBufferContext, syncDraftInputDirtyStateFromBuffer]);

  const clearDraftInputBufferMachineForCurrentVisit = useCallback((localMachineId) => {
    const context = getDraftInputBufferContext();
    if (!context) return;
    clearCollectionDraftInputBufferMachine({
      ...context,
      localMachineId,
    });
    const readBuffer = readCollectionDraftInputBuffer(context);
    syncDraftInputDirtyStateFromBuffer(readBuffer.buffer);
  }, [getDraftInputBufferContext, syncDraftInputDirtyStateFromBuffer]);

  const clearCurrentDraftState = useCallback(() => {
    clearVisitDraftForTerminalResult({ localId: effectiveLocalId });
    clearDraftInputBufferForCurrentVisit();
    setCreationDraftVisitId(null);
    setDraftProgress(null);
    setSelectedDraftLocalMachineId(null);
    dirtyWasCollectedByLMRef.current = {};
    dirtyDraftInputFieldsRef.current = {};
    userInputsRef.current = {};
    wasCollectedByLMRef.current = {};
    setWasCollectedByLM({});
    setUserInputs({});
    setSettledInputFields({});
    setVisitMachineDetails([]);
    setCounterVerificationContextsByLocalMachineId({});
    setIsDraftDateChangeRequested(false);
    setOpenDraftDateResetDialog(false);
  }, [effectiveLocalId, clearDraftInputBufferForCurrentVisit]);

  const markDraftTerminalAndClear = useCallback(() => {
    draftTerminalRef.current = true;
    clearCurrentDraftState();
  }, [clearCurrentDraftState]);

  const tryBeginDraftAction = useCallback((action) => {
    if (savingRef.current || draftDateSavingRef.current || draftActionRef.current) return false;
    draftActionRef.current = action;
    setDraftAction(action);
    return true;
  }, []);

  const finishDraftAction = useCallback(() => {
    draftActionRef.current = null;
    setDraftAction(null);
  }, []);

  useEffect(() => {
    if (isEditMode) return;
    if (draftTerminalRef.current) return;
    if (visitWithCollectionId) return;
    if (!effectiveLocalId) return;

    let alive = true;
    setIsLoadingDraftProgress(true);
    (async () => {
      try {
        const draft = await VisitTransactionService.startOrResumeCollectionDraft(effectiveLocalId);
        if (!alive) return;
        const nextVisitId = Number(draft?.visitWithCollectionId ?? draft?.visitId ?? 0);
        if (!nextVisitId) throw new Error("No se pudo resolver el borrador de recaudación.");
        setCreationDraftVisitId(nextVisitId);
        setVisitDateInput(toDateInputValue(draft?.visitDateTime || new Date()));
        rememberVisitDraft(effectiveLocalId, nextVisitId);
      } catch (error) {
        if (!alive) return;
        const errorInfo = getVisitTransactionSaveErrorInfo(error);
        notifyError(errorInfo.message || "No se pudo iniciar la recaudación.");
      } finally {
        if (alive) setIsLoadingDraftProgress(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isEditMode, visitWithCollectionId, effectiveLocalId, notifyError]);

  const refreshDraftProgress = useCallback(async () => {
    if (isEditMode || !visitWithCollectionId) return null;
    setIsLoadingDraftProgress(true);
    try {
      const progress = await VisitTransactionService.getDraftProgress(visitWithCollectionId);
      setDraftProgress(progress);
      if (progress?.visit?.DateTime) {
        setVisitDateInput(toDateInputValue(progress.visit.DateTime));
      }
      const expectedMachines = Array.isArray(progress?.expectedMachines)
        ? progress.expectedMachines
        : [];
      if (expectedMachines.length) {
        setLocalMachineDetails(expectedMachines);
        setMachineTypes(getUniqueMachineTypes(expectedMachines));
      }
      const persisted = buildDraftInputHydrationMachines(progress);
      const hydratedInputs = persisted.length ? buildInputsFromMachines(persisted) : {};
      const hydratedWasCollected = persisted.reduce((acc, machine) => {
        const key = Number(machine?.LocalMachineId);
        if (Number.isFinite(key) && key > 0) {
          acc[key] = isPersistedCollectedMachine(machine);
        }
        return acc;
      }, {});
      const restorationBackendWasCollected = expectedMachines.reduce((acc, machine) => {
        const key = Number(machine?.LocalMachineId ?? machine?.Id ?? 0);
        if (!Number.isFinite(key) || key <= 0) return acc;
        acc[key] = typeof hydratedWasCollected[key] === "boolean"
          ? hydratedWasCollected[key]
          : getDraftMachineDefaultWasCollected(machine);
        return acc;
      }, {});
      const mergedInputs = mergeDraftHydratedInputs({
        prevInputs: userInputsRef.current,
        hydratedInputs,
        dirtyWasCollectedByLM: dirtyWasCollectedByLMRef.current,
      });
      const mergedWasCollected = mergeDraftHydratedWasCollected({
        prevWasCollectedByLM: wasCollectedByLMRef.current,
        hydratedWasCollectedByLM: hydratedWasCollected,
        dirtyWasCollectedByLM: dirtyWasCollectedByLMRef.current,
      });
      const bufferContext = getDraftInputBufferContext();
      let nextInputs = mergedInputs;
      let nextWasCollected = mergedWasCollected;
      let restoredCount = 0;
      let conflictCount = 0;

      if (bufferContext) {
        const readBuffer = readCollectionDraftInputBuffer(bufferContext);
        if (readBuffer.buffer) {
          const restoration = restoreCollectionDraftInputBuffer({
            buffer: readBuffer.buffer,
            ...bufferContext,
            visitStatus: progress?.visit?.Status,
            expectedMachineIds: expectedMachines.map((machine) => machine?.LocalMachineId ?? machine?.Id),
            backendInputs: hydratedInputs,
            backendWasCollectedByLM: restorationBackendWasCollected,
            currentInputs: mergedInputs,
            currentWasCollectedByLM: mergedWasCollected,
          });
          nextInputs = restoration.userInputs;
          nextWasCollected = restoration.wasCollectedByLM;
          restoredCount = restoration.restored.length;
          conflictCount = restoration.conflicts.length;
          syncDraftInputDirtyStateFromBuffer(
            restoration.shouldDelete ? null : restoration.nextBuffer
          );
          if (restoration.shouldDelete) {
            clearCollectionDraftInputBuffer(bufferContext);
          } else if (restoration.nextBuffer) {
            writeCollectionDraftInputBuffer({ buffer: restoration.nextBuffer });
          }
        }
      }
      userInputsRef.current = nextInputs;
      wasCollectedByLMRef.current = nextWasCollected;
      setUserInputs(nextInputs);
      setWasCollectedByLM(nextWasCollected);
      setDraftInputBufferConflictCount(conflictCount);
      if (restoredCount > 0) {
        notifySuccess("Se recuperaron cambios no guardados de esta sesión.");
      }
      return progress;
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      if (
        errorInfo.code === "COLLECTION_DRAFT_NOT_FOUND" ||
        errorInfo.code === "COLLECTION_DRAFT_ALREADY_COMPLETE"
      ) {
        markDraftTerminalAndClear();
        if (errorInfo.code === "COLLECTION_DRAFT_ALREADY_COMPLETE") {
          const destination = getVisitDraftTerminalDestination({
            outcome: "alreadyComplete",
            localId: local?.Id ?? state?.localId ?? id,
            visitId: visitWithCollectionId,
          });
          navigate(destination.path, {
            replace: true,
            state: { local, returnTo: state?.returnTo || null },
          });
        } else {
          handleBackToLocal();
        }
        return null;
      }
      notifyError(errorInfo.message || "No se pudo obtener el progreso de la recaudación.");
      return null;
    } finally {
      setIsLoadingDraftProgress(false);
    }
  }, [
    isEditMode,
    visitWithCollectionId,
    notifyError,
    notifySuccess,
    buildInputsFromMachines,
    getDraftInputBufferContext,
    syncDraftInputDirtyStateFromBuffer,
    markDraftTerminalAndClear,
    local,
    state,
    id,
    navigate,
  ]);

  useEffect(() => {
    if (isEditMode || !visitWithCollectionId) return;
    refreshDraftProgress();
  }, [isEditMode, visitWithCollectionId, refreshDraftProgress]);
  const orderedLocalMachineDetails = useMemo(
    () => sortLocalMachinesFixed(localMachineDetails, { preferLocalDisplayNumber: true }),
    [localMachineDetails]
  );
  const visibleLocalMachineDetails = useMemo(() => {
    const dateInputForMachineContext = isEditMode ? machineContextDateInput : draftPersistedDateInput;
    const selectedBusinessDate =
      (typeof dateInputForMachineContext === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInputForMachineContext)
        ? dateInputForMachineContext
        : toBusinessDateKey(effectiveVisitDateTimeForPreview)) || null;

    if (!selectedBusinessDate) {
      return orderedLocalMachineDetails;
    }

    return orderedLocalMachineDetails.filter((machine) => {
      const keyId = resolveMachineKey(machine);
      const visitDetail = (visitMachineDetails || []).find(
        (detail) => Number(detail?.LocalMachineId) === keyId
      );
      const mergedMachine = mergeMachineWithVisitDetail(machine, visitDetail);
      const existenceDateRaw =
        mergedMachine?.InstallationStartDateTime ??
        mergedMachine?.StartDateTime ??
        null;

      if (!existenceDateRaw) return true;
      const existenceBusinessDate = toBusinessDateKey(existenceDateRaw);
      if (!existenceBusinessDate) return true;

      return selectedBusinessDate >= existenceBusinessDate;
    });
  }, [
    isEditMode,
    draftPersistedDateInput,
    machineContextDateInput,
    effectiveVisitDateTimeForPreview,
    orderedLocalMachineDetails,
    visitMachineDetails,
    mergeMachineWithVisitDetail,
    toBusinessDateKey,
  ]);

  const counterContextCandidateMachines = useMemo(() => {
    return (visibleLocalMachineDetails || []).filter((machine) => {
      const localMachineId = resolveMachineKey(machine);
      const typeName = machine?.MachineType?.Type || machine?.MachineType || machine?.MachineTypeType;
      return (
        Number.isFinite(localMachineId) &&
        localMachineId > 0 &&
        typeName === "Tragamoneda" &&
        !hasRent(machine)
      );
    });
  }, [visibleLocalMachineDetails]);

  const firstHistoricalMachineStartDateKey = useMemo(
    () => toBusinessDateKey(firstHistoricalMachineStart),
    [firstHistoricalMachineStart, toBusinessDateKey]
  );

  const latestActiveMachineStartDateKey = useMemo(
    () => toBusinessDateKey(latestActiveMachineStart),
    [latestActiveMachineStart, toBusinessDateKey]
  );

  const latestInactiveMachineFinishDateKey = useMemo(
    () => toBusinessDateKey(latestInactiveMachineFinishDate),
    [latestInactiveMachineFinishDate, toBusinessDateKey]
  );

  const editSourceMachines = useMemo(() => {
    if (!isEditMode) return [];
    if (Array.isArray(bootCalc) && bootCalc.length) {
      return bootCalc
        .map(synthesizeHistoricalPreviousValues)
        .map(normalizeCompletedEditDetailMachine);
    }
    if (Array.isArray(state?.calculatedVisitMachines) && state.calculatedVisitMachines.length) {
      return state.calculatedVisitMachines
        .map(synthesizeHistoricalPreviousValues)
        .map(normalizeCompletedEditDetailMachine);
    }
    return [];
  }, [
    isEditMode,
    bootCalc,
    state?.calculatedVisitMachines,
    synthesizeHistoricalPreviousValues,
  ]);

  const completedEditPersistedBaseline = useMemo(
    () =>
      getCompletedEditPersistedBaseline({
        editMachineRows,
        editSourceMachines,
      }),
    [editMachineRows, editSourceMachines]
  );

  const getEditInputBufferContext = useCallback(() => {
    if (!isEditMode) return null;
    const userKey = getCollectionDraftInputBufferUserKey();
    const parsedVisitId = Number(effectiveEditVisitId);
    const parsedLocalId = Number(effectiveLocalId);
    if (!userKey || !Number.isFinite(parsedVisitId) || parsedVisitId <= 0) return null;
    if (!Number.isFinite(parsedLocalId) || parsedLocalId <= 0) return null;
    return {
      userKey,
      visitId: parsedVisitId,
      localId: parsedLocalId,
    };
  }, [isEditMode, effectiveEditVisitId, effectiveLocalId]);

  const syncEditInputDirtyStateFromBuffer = useCallback((buffer = null) => {
    const dirtyMachineIds = getCollectionEditInputBufferDirtyMachineIds(buffer);
    setEditInputDirtyMachineIds(dirtyMachineIds);
    if (isEditMode) setIsCollectionDirty(dirtyMachineIds.length > 0);
    return dirtyMachineIds;
  }, [isEditMode]);

  const getEditBaselineInputs = useCallback(() => {
    if (!isEditMode) return {};
    return buildInputsFromMachines(completedEditPersistedBaseline);
  }, [isEditMode, completedEditPersistedBaseline, buildInputsFromMachines]);

  const getEditFieldSnapshot = useCallback((localMachineId, field) => {
    const lmId = Number(localMachineId);
    const baselineInputs = getEditBaselineInputs();
    const inputs = baselineInputs?.[lmId] ?? baselineInputs?.[String(lmId)] ?? {};
    if (field === "WasCollected") {
      const baselineMap = buildCompletedEditWasCollectedMap(completedEditPersistedBaseline);
      if (typeof baselineMap?.[lmId] === "boolean") {
        return { exists: true, value: baselineMap[lmId] };
      }
      if (typeof baselineMap?.[String(lmId)] === "boolean") {
        return { exists: true, value: baselineMap[String(lmId)] };
      }
    }
    if (Object.prototype.hasOwnProperty.call(inputs || {}, field)) {
      return { exists: true, value: inputs[field] };
    }
    return { exists: false, value: undefined };
  }, [getEditBaselineInputs, completedEditPersistedBaseline]);

  const getEditBaseVisitMachineId = useCallback((localMachineId) => {
    const lmId = Number(localMachineId);
    const persisted = (completedEditPersistedBaseline || []).find(
      (machine) => Number(machine?.LocalMachineId ?? machine?.Id) === lmId
    );
    return resolveVisitMachineId(persisted);
  }, [completedEditPersistedBaseline]);

  const markEditInputFieldDirty = useCallback((localMachineId, field, localValue, baseSnapshot = null) => {
    const context = getEditInputBufferContext();
    const lmId = Number(localMachineId);
    if (!context || !Number.isFinite(lmId) || lmId <= 0) return;
    const snapshot = baseSnapshot || getEditFieldSnapshot(lmId, field);
    updateCollectionEditInputBufferField({
      ...context,
      localMachineId: lmId,
      visitMachineId: getEditBaseVisitMachineId(lmId),
      field,
      baseValue: snapshot.value,
      baseExists: snapshot.exists,
      localValue,
    });
    const readBuffer = readCollectionEditInputBuffer(context);
    syncEditInputDirtyStateFromBuffer(readBuffer.buffer);
  }, [
    getEditInputBufferContext,
    getEditFieldSnapshot,
    getEditBaseVisitMachineId,
    syncEditInputDirtyStateFromBuffer,
  ]);

  const clearEditInputBufferForCurrentVisit = useCallback(() => {
    const context = getEditInputBufferContext();
    if (!context) return;
    clearCollectionEditInputBuffer(context);
    syncEditInputDirtyStateFromBuffer(null);
    setEditInputBufferConflictCount(0);
  }, [getEditInputBufferContext, syncEditInputDirtyStateFromBuffer]);

  const clearEditInputBufferMachineForCurrentVisit = useCallback((localMachineId) => {
    const context = getEditInputBufferContext();
    if (!context) return;
    clearCollectionEditInputBufferMachine({
      ...context,
      localMachineId,
    });
    const readBuffer = readCollectionEditInputBuffer(context);
    syncEditInputDirtyStateFromBuffer(readBuffer.buffer);
  }, [getEditInputBufferContext, syncEditInputDirtyStateFromBuffer]);

  // obtener registros previos para edición
  const prevRecords = useMemo(() => {
    if (!isEditMode) return [];
    return completedEditPersistedBaseline;
  }, [isEditMode, completedEditPersistedBaseline]);

  const editHistoricalMachines = useMemo(() => {
    if (!isEditMode) return [];
    return completedEditPersistedBaseline;
  }, [isEditMode, completedEditPersistedBaseline]);

  useEffect(() => {
    if (
      bootLoading ||
      !shouldInitializeCompletedEditBaseline({
        isEditMode,
        effectiveVisitId: effectiveEditVisitId,
        loadedVisitId: bootLoadedVisitId,
        initializedVisitId: initializedCompletedEditVisitIdRef.current,
        sourceRows: editSourceMachines,
      })
    ) {
      return;
    }
    const baselineInputs = buildInputsFromMachines(editSourceMachines);
    const baselineWasCollected = editSourceMachines.reduce((acc, machine) => {
      const key = Number(machine?.LocalMachineId);
      if (Number.isFinite(key) && key > 0) {
        acc[key] = isPersistedCollectedMachine(machine);
      }
      return acc;
    }, {});
    const bufferContext = getEditInputBufferContext();
    let nextInputs = baselineInputs;
    let nextWasCollected = baselineWasCollected;
    let restoredCount = 0;
    let conflictCount = 0;

    if (bufferContext) {
      const readBuffer = readCollectionEditInputBuffer(bufferContext);
      if (readBuffer.buffer) {
        const restoration = restoreCollectionEditInputBuffer({
          buffer: readBuffer.buffer,
          ...bufferContext,
          expectedMachineIds: editSourceMachines.map((machine) => machine?.LocalMachineId ?? machine?.Id),
          backendInputs: baselineInputs,
          backendWasCollectedByLM: baselineWasCollected,
          currentInputs: baselineInputs,
          currentWasCollectedByLM: baselineWasCollected,
        });
        nextInputs = restoration.userInputs;
        nextWasCollected = restoration.wasCollectedByLM;
        restoredCount = restoration.restored.length;
        conflictCount = restoration.conflicts.length;
        syncEditInputDirtyStateFromBuffer(
          restoration.shouldDelete ? null : restoration.nextBuffer
        );
        if (restoration.shouldDelete) {
          clearCollectionEditInputBuffer(bufferContext);
        } else if (restoration.nextBuffer) {
          writeCollectionEditInputBuffer({ buffer: restoration.nextBuffer });
        }
      } else {
        syncEditInputDirtyStateFromBuffer(null);
      }
    }
    userInputsRef.current = nextInputs;
    wasCollectedByLMRef.current = nextWasCollected;
    setUserInputs(nextInputs);
    setEditMachineRows(editSourceMachines);
    setWasCollectedByLM(nextWasCollected);
    setSelectedEditVisitMachineId(null);
    initializedCompletedEditVisitIdRef.current = Number(effectiveEditVisitId);
    setEditInputBufferConflictCount(conflictCount);
    if (restoredCount > 0) {
      notifySuccess("Se recuperaron cambios no guardados de esta sesión.");
    }
  }, [
    isEditMode,
    effectiveEditVisitId,
    bootLoadedVisitId,
    bootLoading,
    editSourceMachines,
    buildInputsFromMachines,
    getEditInputBufferContext,
    syncEditInputDirtyStateFromBuffer,
    notifySuccess,
    notifyError,
  ]);

  useEffect(() => {
    if (!isEditMode) return;
    const visitIdNumber = Number(effectiveEditVisitId);
    if (!Number.isFinite(visitIdNumber) || visitIdNumber <= 0) return;
    if (lastEffectiveEditVisitIdRef.current == null) {
      lastEffectiveEditVisitIdRef.current = visitIdNumber;
      return;
    }
    if (
      Number(lastEffectiveEditVisitIdRef.current) !== visitIdNumber ||
      (
        initializedCompletedEditVisitIdRef.current != null &&
        Number(initializedCompletedEditVisitIdRef.current) !== visitIdNumber
      )
    ) {
      lastEffectiveEditVisitIdRef.current = visitIdNumber;
      initializedCompletedEditVisitIdRef.current = null;
      setEditMachineRows([]);
      dirtyWasCollectedByLMRef.current = {};
      setUserInputs({});
      setWasCollectedByLM({});
      setVisitMachineDetails([]);
      setCounterVerificationContextsByLocalMachineId({});
      setEditTotals(null);
      setHistoricalEditDateError(null);
      setSelectedEditVisitMachineId(null);
      setEditInputDirtyMachineIds([]);
      setEditInputBufferConflictCount(0);
      setIsCollectionDirty(false);
    }
  }, [isEditMode, effectiveEditVisitId]);

  useEffect(() => {
    if (!isEditMode) return;
    setEditTotals(
      getCompletedEditPersistedTotals({
        state,
        visitWithCollection: bootVisitWithCollection,
        bootDetail: { visitWithCollection: bootVisitWithCollection },
      }) || {
        TotalAmount: 0,
        TotalClientAmount: 0,
        TotalOwnerAmount: 0,
      }
    );
  }, [isEditMode, state, bootVisitWithCollection]);

  useEffect(() => {
    if (!isEditMode) return;
    const localIdForEdit = Number(local?.Id ?? id);
    const editVisitId = Number(visitWithCollectionId ?? visitId);
    if (!Number.isFinite(localIdForEdit) || !Number.isFinite(editVisitId)) return;

    let alive = true;
    (async () => {
      try {
        const last = await VisitTransactionService.getLastVisitTransactionByLocal(localIdForEdit);
        if (!alive) return;
        const lastId = Number(last?.visit?.Id ?? 0);
        if (lastId !== editVisitId) {
          notifyError("Solo se puede editar la última recaudación del local.");
          navigate(`/local/${localIdForEdit}/recaudaciones/${editVisitId}`, {
            replace: true,
            state: { local, returnTo: state?.returnTo || null },
          });
        }
      } catch (_e) {
        if (!alive) return;
        notifyError("No se pudo validar si esta recaudación es la última.");
        navigate(`/local/${localIdForEdit}/recaudaciones/${editVisitId}`, {
          replace: true,
          state: { local, returnTo: state?.returnTo || null },
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [isEditMode, local?.Id, id, visitWithCollectionId, visitId, navigate, notifyError, local]);

  useEffect(() => {
    const sourceDate = isEditMode
      ? state?.visitDateTime || bootVisitDateTime || null
      : draftProgress?.visit?.DateTime || state?.visitDateTime || bootVisitDateTime || null;
    if (!sourceDate) return;
    if (!isEditMode && isDraftDateChangeRequested) return;
    const nextDateInput = toDateInputValue(sourceDate);
    setVisitDateInput(nextDateInput);
    if (isEditMode) setEditPersistedDateInput(nextDateInput);
  }, [
    state?.visitDateTime,
    bootVisitDateTime,
    isEditMode,
    draftProgress?.visit?.DateTime,
    isDraftDateChangeRequested,
  ]);

  useEffect(() => {
    if (!effectiveLocalId) return;
    if (isEditMode && !effectiveEditVisitId) return;

    let alive = true;
    setIsLoadingCollectionDateBounds(true);
    (async () => {
      try {
        const bounds = await VisitTransactionService.getCollectionDateBounds(effectiveLocalId, {
          visitId: isEditMode ? effectiveEditVisitId : visitWithCollectionId ?? null,
        });
        if (!alive || !bounds) return;
        setCollectionDateBounds({
          MinSelectableCollectionDate: bounds.MinSelectableCollectionDate ?? null,
          MaxSelectableCollectionDate:
            bounds.MaxSelectableCollectionDate ?? toDateInputValue(new Date()),
          ReferenceKind: bounds.ReferenceKind ?? "none",
          ReferenceDate: bounds.ReferenceDate ?? null,
        });
      } catch (_e) {
        if (!alive) return;
        setCollectionDateBounds((prev) => ({
          ...prev,
          MaxSelectableCollectionDate: prev?.MaxSelectableCollectionDate ?? toDateInputValue(new Date()),
        }));
      } finally {
        if (alive) setIsLoadingCollectionDateBounds(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [effectiveLocalId, effectiveEditVisitId, visitWithCollectionId, isEditMode]);

  useEffect(() => {
    if (!isEditMode || !effectiveEditVisitId) {
      setCollectionEditSafeBounds(null);
      setCollectionEditSafeBoundsError(null);
      return;
    }

    let alive = true;
    setIsLoadingCollectionEditSafeBounds(true);
    setCollectionEditSafeBoundsError(null);
    (async () => {
      try {
        const bounds =
          await VisitTransactionService.getCollectionEditSafeDateBounds(
            effectiveEditVisitId
          );
        if (!alive) return;
        setCollectionEditSafeBounds(bounds);
      } catch (error) {
        if (!alive) return;
        const errorInfo = getVisitTransactionSaveErrorInfo(error);
        setCollectionEditSafeBounds(null);
        setCollectionEditSafeBoundsError(
          errorInfo.message || "No se pudo calcular el rango histórico seguro."
        );
      } finally {
        if (alive) setIsLoadingCollectionEditSafeBounds(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [effectiveEditVisitId, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;
    const localIdForEdit = Number(local?.Id ?? id);
    const editVisitId = Number(visitWithCollectionId ?? visitId);
    if (!Number.isFinite(localIdForEdit) || !Number.isFinite(editVisitId)) return;
    if (lastLocalVisitBeforeId != null) return;

    let alive = true;
    (async () => {
      try {
        const detail = await VisitTransactionService.getVisitTransactionDetails(String(editVisitId), {
          localId: String(localIdForEdit),
        });
        if (!alive || !detail) return;
        setLastLocalVisitBeforeId(detail?.LastLocalVisitBeforeId ?? null);
        setLastLocalVisitBeforeDate(detail?.LastLocalVisitBeforeDate ?? null);
      } catch (_e) {
        // noop: hay otros caminos de carga y este fetch solo completa metadata faltante
      }
    })();

    return () => {
      alive = false;
    };
  }, [isEditMode, local?.Id, id, visitWithCollectionId, visitId, lastLocalVisitBeforeId]);

  useEffect(() => {
    if (!isEditMode) return;
    setLastLocalVisitBeforeId((prev) =>
      prev ?? state?.LastLocalVisitBeforeId ?? state?.lastLocalVisitBeforeId ?? bootLastLocalVisitBeforeId ?? null
    );
    setLastLocalVisitBeforeDate((prev) =>
      prev ??
      state?.LastLocalVisitBeforeDate ??
      state?.lastLocalVisitBeforeDate ??
      bootLastLocalVisitBeforeDate ??
      null
    );
  }, [
    isEditMode,
    state?.LastLocalVisitBeforeId,
    state?.lastLocalVisitBeforeId,
    state?.LastLocalVisitBeforeDate,
    state?.lastLocalVisitBeforeDate,
    bootLastLocalVisitBeforeId,
    bootLastLocalVisitBeforeDate,
  ]);

  useEffect(() => {
    if (!local) return;
    setIsLoading(true);
    // bloque de edición
    if (isEditMode) {
      if (bootLoading) return;
      const currentEditLocalMachineRows =
        Array.isArray(bootLM) && bootLM.length
          ? bootLM
          : Array.isArray(state?.localMachineDetails)
            ? state.localMachineDetails
            : [];
      const editLocalMachineRows = buildHistoricalEditLocalMachineRows(
        currentEditLocalMachineRows,
        editHistoricalMachines
      );
      const nextProfitPercentages =
        Array.isArray(bootProfits) && bootProfits.length
          ? onlyActiveProfit(bootProfits)
          : onlyActiveProfit(state?.profitPercentages || []);
      const historicalDates = editLocalMachineRows
        .map((machine) => machine?.StartDateTime ?? null)
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()));
      const inactiveFinishDates = editLocalMachineRows
        .filter((machine) => machine?.IsActive === false)
        .map((machine) => machine?.FinishDateTime ?? null)
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()));
      const activeDates = editLocalMachineRows
        .filter((machine) => machine?.IsActive !== false)
        .map((machine) => machine?.StartDateTime ?? null)
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()));

      setLocalMachineDetails(editLocalMachineRows);
      setProfitPercentages(nextProfitPercentages);
      setMachineTypes(getUniqueMachineTypes(editLocalMachineRows));
      setFirstHistoricalMachineStart(
        historicalDates.length
          ? historicalDates.reduce((earliest, current) =>
              current.getTime() < earliest.getTime() ? current : earliest
            )
          : null
      );
      setLatestActiveMachineStart(
        activeDates.length
          ? activeDates.reduce((latest, current) =>
              current.getTime() > latest.getTime() ? current : latest
            )
          : null
      );
      setLatestInactiveMachineFinishDate(
        inactiveFinishDates.length
          ? inactiveFinishDates.reduce((latest, current) =>
              current.getTime() > latest.getTime() ? current : latest
            )
          : null
      );
      setLastLocalVisitBeforeId(
        state?.LastLocalVisitBeforeId ??
          state?.lastLocalVisitBeforeId ??
          bootLastLocalVisitBeforeId ??
          null
      );
      setLastLocalVisitBeforeDate(
        state?.LastLocalVisitBeforeDate ??
          state?.lastLocalVisitBeforeDate ??
          bootLastLocalVisitBeforeDate ??
          null
      );
      setIsLoading(false);
      return;
    }
    // modo creación
    let alive = true;
    (async () => {
      try {
        const stateLocalMachineDetails = getLocalMachinePresentationDetails(
          state?.localMachineDetails,
          local.Id
        );
        const raw = stateLocalMachineDetails.length
          ? stateLocalMachineDetails
          : await LocalMachineService.getLocalMachineByLocal(local.Id);
        const details = onlyActive(raw);
        const historicalDates = (raw || [])
          .map((machine) => machine?.StartDateTime ?? null)
          .filter(Boolean)
          .map((value) => new Date(value))
          .filter((date) => !Number.isNaN(date.getTime()));
        const inactiveFinishDates = (raw || [])
          .filter((machine) => machine?.IsActive === false)
          .map((machine) => machine?.FinishDateTime ?? null)
          .filter(Boolean)
          .map((value) => new Date(value))
          .filter((date) => !Number.isNaN(date.getTime()));
        const activeDates = (details || [])
          .map((machine) => machine?.StartDateTime ?? null)
          .filter(Boolean)
          .map((value) => new Date(value))
          .filter((date) => !Number.isNaN(date.getTime()));
        if (process.env.NODE_ENV !== "production") {
          console.log("LocalMachineDetails cargadas:", details);
        }
        if (!alive) return;
        setLocalMachineDetails(details);
        setLocalMachinePresentationDetails(details);
        setFirstHistoricalMachineStart(
          historicalDates.length
            ? historicalDates.reduce((earliest, current) =>
                current.getTime() < earliest.getTime() ? current : earliest
              )
            : null
        );
        setLatestActiveMachineStart(
          activeDates.length
            ? activeDates.reduce((latest, current) =>
                current.getTime() > latest.getTime() ? current : latest
              )
            : null
        );
        setLatestInactiveMachineFinishDate(
          inactiveFinishDates.length
            ? inactiveFinishDates.reduce((latest, current) =>
                current.getTime() > latest.getTime() ? current : latest
              )
            : null
        );

        setMachineTypes(getUniqueMachineTypes(details));

         if (!state?.profitPercentages) {
          const profit = await LocalMachineProfitPercentageService.getLocalMachineProfitPercentagesByLocal(
            local.Id
          );
          setProfitPercentages(onlyActiveProfit(profit));
        }
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error cargando datos:", err);
        }
        notifyError("No se pudieron cargar los datos del local.");
      } finally {
               setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [local, isEditMode, state?.localMachineDetails, state?.profitPercentages, editHistoricalMachines, buildHistoricalEditLocalMachineRows, bootLoading, bootLM, bootProfits, bootCalc, editSourceMachines, state?.calculatedVisitMachines]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshCollectionPreviousValues = useCallback(async ({ force = false } = {}) => {
    const localId = Number(effectiveLocalId);
    if (!Number.isFinite(localId) || localId <= 0) return false;
    const excludeVisitId = isEditMode ? Number(visitWithCollectionId ?? visitId ?? 0) : null;
    const requestKey = buildCollectionPreviousValuesRequestKey({
      localId,
      visitDateTime: effectiveVisitDateTimeForPreview,
      isEditMode,
      excludeVisitId,
    });
    if (!requestKey) return false;
    const nextRequest = getNextCollectionPreviousValuesRequestState({
      currentLoading: previousValuesLoadingRef.current,
      requestKey,
      currentRequestId: previousValuesRequestIdRef.current,
      force,
    });
    if (!nextRequest.shouldStart) return false;

    const { requestId } = nextRequest;
    previousValuesRequestIdRef.current = requestId;
    previousValuesLoadingRef.current = nextRequest.loading;
    try {
      const last = await VisitMachineService.getCollectionPreviousValuesByLocal(
        localId,
        effectiveVisitDateTimeForPreview,
        isEditMode
          ? { excludeVisitId }
          : undefined
      );
      if (requestId !== previousValuesRequestIdRef.current) return false;

      const normalizedRows = normalizeCollectionPreviousValueRows(last || []);
      setVisitMachineDetails(normalizedRows);

      if (!isEditMode) {
        const global = normalizedRows.length ? normalizedRows[0] : null;
        setLastLocalVisitId(global?.LastLocalVisitId ?? null);
        setLastLocalVisitDate(global?.LastLocalVisitDate ?? null);
      }
      return true;
    } catch (err) {
      if (requestId !== previousValuesRequestIdRef.current) return false;
      if (process.env.NODE_ENV !== "production") {
        console.error("Error refrescando previos por fecha:", err);
      }
      notifyError("No se pudieron refrescar los previos de recaudación para la fecha seleccionada.");
      return false;
    } finally {
      if (previousValuesLoadingRef.current?.requestId === requestId) {
        previousValuesLoadingRef.current = null;
      }
    }
  }, [
    effectiveLocalId,
    effectiveVisitDateTimeForPreview,
    isEditMode,
    notifyError,
    visitWithCollectionId,
    visitId,
  ]);

  useEffect(() => {
    let alive = true;

    (async () => {
      await refreshCollectionPreviousValues();
      if (!alive) return;
    })();

    return () => {
      alive = false;
    };
  }, [refreshCollectionPreviousValues, resetVersion]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleOnline = () => {
      refreshCollectionPreviousValues({ force: true });
    };
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [
    refreshCollectionPreviousValues,
  ]);

  useEffect(() => {
    if (!counterContextCandidateMachines.length) {
      setCounterVerificationContextsByLocalMachineId({});
      return;
    }

    let alive = true;
    const beforeVisitId =
      isEditMode && Number.isFinite(Number(visitWithCollectionId ?? visitId))
        ? Number(visitWithCollectionId ?? visitId)
        : null;

    (async () => {
      const entries = await Promise.all(
        counterContextCandidateMachines.map(async (machine) => {
          const localMachineId = resolveMachineKey(machine);
          const context =
            await VisitMachineCounterVerificationService.getLocalMachineCounterVerificationContext(
              localMachineId,
              {
                ...(effectiveVisitDateTimeForPreview
                  ? { visitDateTime: effectiveVisitDateTimeForPreview }
                  : {}),
                ...(beforeVisitId ? { beforeVisitId } : {}),
              }
            );
          return [localMachineId, context];
        })
      );

      if (!alive) return;
      setCounterVerificationContextsByLocalMachineId(
        entries.reduce((acc, [localMachineId, context]) => {
          acc[localMachineId] = context;
          return acc;
        }, {})
      );
    })();

    return () => {
      alive = false;
    };
  }, [
    counterContextCandidateMachines,
    effectiveVisitDateTimeForPreview,
    isEditMode,
    visitWithCollectionId,
    visitId,
  ]);

  // - fallback para edición sin state
  useEffect(() => {
    if (!isEditMode) return;
    if (Number(bootLoadedVisitId) !== Number(effectiveEditVisitId)) return;
    const missing =
      !state?.calculatedVisitMachines ||
      !state?.localMachineDetails ||
      !state?.profitPercentages;

    if (missing && bootCalc?.length) {
      const fallbackEditLocalMachineRows = buildHistoricalEditLocalMachineRows(
        bootLM || [],
        completedEditPersistedBaseline
      );
      setLocalMachineDetails(fallbackEditLocalMachineRows);
      setProfitPercentages(onlyActiveProfit(bootProfits || []));
      setMachineTypes(getUniqueMachineTypes(fallbackEditLocalMachineRows));

      // prevCredits para Anterior
      setLastLocalVisitBeforeId((prev) => prev ?? bootLastLocalVisitBeforeId ?? null);
      setLastLocalVisitBeforeDate((prev) => prev ?? bootLastLocalVisitBeforeDate ?? null);
    }
  }, [
    isEditMode,
    state,
    bootCalc,
    bootLoadedVisitId,
    bootLM,
    bootProfits,
    bootPrevCredits,
    bootLastLocalVisitBeforeId,
    bootLastLocalVisitBeforeDate,
    buildHistoricalEditLocalMachineRows,
    completedEditPersistedBaseline,
    effectiveEditVisitId,
  ]);

  // Manejar cambios en los inputs
  const handleInputChange = useCallback((machineId, field, value) => {
    if (isEditMode) {
      const snapshot = getEditFieldSnapshot(machineId, field);
      markEditInputFieldDirty(machineId, field, value, snapshot);
    } else {
      const snapshot = getDraftFieldSnapshot(machineId, field);
      markDraftInputFieldDirty(machineId, field, value, snapshot);
    }
    const fieldKey = `${machineId}:${field}`;
    if (inputSettleTimersRef.current[fieldKey]) {
      clearTimeout(inputSettleTimersRef.current[fieldKey]);
    }
    setSettledInputFields((prev) => ({
      ...prev,
      [machineId]: {
        ...prev[machineId],
        [field]: false,
      },
    }));
    inputSettleTimersRef.current[fieldKey] = setTimeout(() => {
      setSettledInputFields((prev) => ({
        ...prev,
        [machineId]: {
          ...prev[machineId],
          [field]: true,
        },
      }));
      delete inputSettleTimersRef.current[fieldKey];
    }, 600);
    setUserInputs((prev) => {
      const before = prev?.[machineId];
      const after = {
        ...before,
        [field]: value,
      };
      const nextInputs = {
        ...prev,
        [machineId]: after,
      };
      userInputsRef.current = nextInputs;
      return nextInputs;
    });
  }, [
    isEditMode,
    getEditFieldSnapshot,
    markEditInputFieldDirty,
    getDraftFieldSnapshot,
    markDraftInputFieldDirty,
  ]);

  useEffect(() => {
    return () => {
      Object.values(inputSettleTimersRef.current || {}).forEach(clearTimeout);
      inputSettleTimersRef.current = {};
    };
  }, []);

  const buildCleanWasCollectedMap = useCallback(() => {
    if (isEditMode) {
      return buildCompletedEditWasCollectedMap(completedEditPersistedBaseline);
    }
    const mergedMap = {};
    const fromEditState = Array.isArray(state?.calculatedVisitMachines)
      ? state.calculatedVisitMachines
      : [];
    const fromBoot = Array.isArray(bootCalc) ? bootCalc : [];
    [...fromEditState, ...fromBoot].forEach((m) => {
      const key = resolveMachineKey(m);
      if (Number.isFinite(key) && key > 0) mergedMap[key] = isPersistedCollectedMachine(m);
    });
    (visibleLocalMachineDetails || []).forEach((m) => {
      const key = resolveMachineKey(m);
      if (
        Number.isFinite(key) &&
        key > 0 &&
        mergedMap[key] == null &&
        !isInsertedInCurrentVisitNoticeMachine(m) &&
        !isPendingInitialValuesNoticeMachine(m)
      ) {
        mergedMap[key] = true;
      }
    });
    return mergedMap;
  }, [
    isEditMode,
    completedEditPersistedBaseline,
    state?.calculatedVisitMachines,
    bootCalc,
    visibleLocalMachineDetails,
  ]);

  const getCleanUserInputs = useCallback(() => {
    if (isEditMode) {
      return buildInputsFromMachines(completedEditPersistedBaseline);
    }
    const sourceMachines =
      Array.isArray(bootCalc) && bootCalc.length
        ? bootCalc
        : Array.isArray(state?.calculatedVisitMachines) && state.calculatedVisitMachines.length
          ? state.calculatedVisitMachines
        : (Array.isArray(bootCalc) ? bootCalc : []);
    return buildInputsFromMachines(sourceMachines);
  }, [
    isEditMode,
    completedEditPersistedBaseline,
    state?.calculatedVisitMachines,
    bootCalc,
    buildInputsFromMachines,
  ]);

  const resolveWasCollectedForToggle = useCallback((localMachineId) => {
    const lmId = Number(localMachineId);
    if (!Number.isFinite(lmId) || lmId <= 0) return true;

    if (typeof wasCollectedByLM?.[lmId] === "boolean") return wasCollectedByLM[lmId];
    if (typeof userInputs?.[lmId]?.WasCollected === "boolean") return userInputs[lmId].WasCollected;

    const source =
      (Array.isArray(completedEditPersistedBaseline)
        ? completedEditPersistedBaseline.find((m) => Number(m?.LocalMachineId ?? m?.Id) === lmId)
        : null) ||
      (Array.isArray(visibleLocalMachineDetails)
        ? visibleLocalMachineDetails.find((m) => Number(m?.LocalMachineId ?? m?.Id) === lmId)
        : null);

    if (typeof source?.WasCollected === "boolean") return source.WasCollected;

    return true;
  }, [wasCollectedByLM, userInputs, completedEditPersistedBaseline, visibleLocalMachineDetails]);

  useEffect(() => {
    if (isCollectionDirty) return;
    const mergedMap = buildCleanWasCollectedMap();
    setWasCollectedByLM((prev) => (
      isEditMode ? { ...prev, ...mergedMap } : { ...mergedMap, ...prev }
    ));
  }, [buildCleanWasCollectedMap, visitMachineDetails, isCollectionDirty, isEditMode]);

  const handleWasCollectedChange = useCallback((localMachineId) => {
    const lmId = Number(localMachineId);
    if (!Number.isFinite(lmId)) return;
    dirtyWasCollectedByLMRef.current = {
      ...dirtyWasCollectedByLMRef.current,
      [lmId]: true,
    };
    const current = resolveWasCollectedForToggle(lmId);
    const nextValue = !current;
    if (isEditMode) {
      markEditInputFieldDirty(lmId, "WasCollected", nextValue, getEditFieldSnapshot(lmId, "WasCollected"));
      setWasCollectedByLM((prev) => {
        const nextMap = { ...prev, [lmId]: nextValue };
        wasCollectedByLMRef.current = nextMap;
        return nextMap;
      });
      setUserInputs((prevInputs) => {
        const nextInputs = {
          ...prevInputs,
          [lmId]: {
            ...prevInputs[lmId],
            WasCollected: nextValue,
          },
        };
        userInputsRef.current = nextInputs;
        return nextInputs;
      });
      return;
    } else {
      markDraftInputFieldDirty(lmId, "WasCollected", nextValue, {
        exists: true,
        value: current,
      });
    }
    const previousInputs = userInputsRef.current?.[lmId] || {};
    const collectedLinkedFields = [
      "CreditsIn",
      "CreditsOut",
      "NetCreditsIn",
      "NetCreditsOut",
      "MoneyIn",
      "MoneyOut",
      "NetMoneyIn",
      "NetMoneyOut",
      "HasReset",
      "PostResetStartIn",
      "PostResetStartOut",
    ];
    const nextLinkedFieldValues = {};
    collectedLinkedFields.forEach((field) => {
      const editBaselineSnapshot = isEditMode ? getEditFieldSnapshot(lmId, field) : null;
      const localValue = field === "HasReset"
        ? (
            nextValue
              ? isEditMode
                ? (editBaselineSnapshot?.exists ? editBaselineSnapshot.value : false)
                : (previousInputs?.HasReset ?? false)
              : false
          )
        : nextValue
          ? isEditMode
            ? (editBaselineSnapshot?.exists ? editBaselineSnapshot.value : previousInputs?.[field] ?? null)
            : (previousInputs?.[field] ?? null)
          : null;
      nextLinkedFieldValues[field] = localValue;
      if (isEditMode) {
        if (editBaselineSnapshot?.exists !== true) return;
        markEditInputFieldDirty(lmId, field, localValue, editBaselineSnapshot);
      } else {
        markDraftInputFieldDirty(lmId, field, localValue, {
          exists: Object.prototype.hasOwnProperty.call(previousInputs, field),
          value: previousInputs?.[field],
        });
      }
    });

    setWasCollectedByLM((prev) => {
      const nextMap = { ...prev, [lmId]: nextValue };
      wasCollectedByLMRef.current = nextMap;
      return nextMap;
    });
    setUserInputs((prevInputs) => {
      const nextInputs = {
        ...prevInputs,
        [lmId]: {
          ...prevInputs[lmId],
          WasCollected: nextValue,
          CreditsIn: nextLinkedFieldValues.CreditsIn,
          CreditsOut: nextLinkedFieldValues.CreditsOut,
          NetCreditsIn: nextLinkedFieldValues.NetCreditsIn,
          NetCreditsOut: nextLinkedFieldValues.NetCreditsOut,
          MoneyIn: nextLinkedFieldValues.MoneyIn,
          MoneyOut: nextLinkedFieldValues.MoneyOut,
          NetMoneyIn: nextLinkedFieldValues.NetMoneyIn,
          NetMoneyOut: nextLinkedFieldValues.NetMoneyOut,
          HasReset: nextLinkedFieldValues.HasReset,
          PostResetStartIn: nextLinkedFieldValues.PostResetStartIn,
          PostResetStartOut: nextLinkedFieldValues.PostResetStartOut,
        },
      };
      userInputsRef.current = nextInputs;
      return nextInputs;
    });
  }, [
    resolveWasCollectedForToggle,
    markDraftInputFieldDirty,
    markEditInputFieldDirty,
    getEditFieldSnapshot,
    isEditMode,
  ]);
  const isResettableTragamoneda = useCallback((machine) => {
    if (!machine) return false;
    const typeName = machine?.MachineType?.Type || machine?.MachineType;
    return typeName === "Tragamoneda";
  }, []);

  const hasOperatorFormula = useCallback((machine) => {
    return hasMachineOperatorFormula(machine);
  }, []);

  const handleToggleReset = useCallback((machine) => {
    if (!isResettableTragamoneda(machine)) return;
    const machineId = Number(machine?.LocalMachineId ?? machine?.Id);
    if (!Number.isFinite(machineId)) return;
    const currentResetInputs = userInputsRef.current?.[machineId] || {};
    const currentHasReset =
      typeof currentResetInputs.HasReset === "boolean"
        ? currentResetInputs.HasReset
        : Boolean(machine?.HasReset);
    const nextResetValues = !currentHasReset
      ? {
          HasReset: true,
          PostResetStartIn: currentResetInputs.PostResetStartIn ?? 0,
          PostResetStartOut: currentResetInputs.PostResetStartOut ?? 0,
        }
      : currentResetInputs.resetBackup
        ? {
            HasReset: Boolean(currentResetInputs.resetBackup.HasReset),
            PostResetStartIn: currentResetInputs.resetBackup.PostResetStartIn ?? null,
            PostResetStartOut: currentResetInputs.resetBackup.PostResetStartOut ?? null,
          }
        : {
            HasReset: false,
            PostResetStartIn: null,
            PostResetStartOut: null,
          };
    ["HasReset", "PostResetStartIn", "PostResetStartOut"].forEach((field) => {
      if (isEditMode) {
        markEditInputFieldDirty(machineId, field, nextResetValues[field], getEditFieldSnapshot(machineId, field));
      } else {
        markDraftInputFieldDirty(machineId, field, nextResetValues[field], {
          exists: Object.prototype.hasOwnProperty.call(currentResetInputs, field),
          value: currentResetInputs[field],
        });
      }
    });

    setUserInputs((prev) => {
      const current = prev[machineId] || {};
      const currentHasReset =
        typeof current.HasReset === "boolean"
          ? current.HasReset
          : Boolean(machine?.HasReset);

      if (!currentHasReset) {
        const backup = current.resetBackup || {
          HasReset: false,
          PostResetStartIn: current.PostResetStartIn ?? null,
          PostResetStartOut: current.PostResetStartOut ?? null,
        };

        const nextInputs = {
          ...prev,
          [machineId]: {
            ...current,
            HasReset: true,
            resetBackup: backup,
            PostResetStartIn: current.PostResetStartIn ?? 0,
            PostResetStartOut: current.PostResetStartOut ?? 0,
          },
        };
        userInputsRef.current = nextInputs;
        return nextInputs;
      }

      if (current.resetBackup) {
        const { resetBackup, ...currentWithoutBackup } = current;
        const nextInputs = {
          ...prev,
          [machineId]: {
            ...currentWithoutBackup,
            HasReset: Boolean(resetBackup.HasReset),
            PostResetStartIn: resetBackup.PostResetStartIn ?? null,
            PostResetStartOut: resetBackup.PostResetStartOut ?? null,
          }
        };
        userInputsRef.current = nextInputs;
        return nextInputs;
      }

      const nextInputs = {
        ...prev,
        [machineId]: {
          ...current,
          HasReset: false,
          PostResetStartIn: null,
          PostResetStartOut: null,
        },
      };
      userInputsRef.current = nextInputs;
      return nextInputs;
    });
  }, [
    isResettableTragamoneda,
    markDraftInputFieldDirty,
    markEditInputFieldDirty,
    getEditFieldSnapshot,
    isEditMode,
  ]);

  const handleResetCollectionDraft = useCallback(() => {
    suppressDirtyRef.current = true;
    dirtyWasCollectedByLMRef.current = {};
    if (isEditMode) {
      clearEditInputBufferForCurrentVisit();
    } else {
      clearDraftInputBufferForCurrentVisit();
    }
    const cleanInputs = getCleanUserInputs();
    const cleanWasCollected = buildCleanWasCollectedMap();
    userInputsRef.current = cleanInputs;
    wasCollectedByLMRef.current = cleanWasCollected;
    setUserInputs(cleanInputs);
    setWasCollectedByLM(cleanWasCollected);
    setIsCollectionDirty(false);
    setResetVersion((prev) => prev + 1);
    setTimeout(() => {
      suppressDirtyRef.current = false;
    }, 0);
  }, [
    isEditMode,
    getCleanUserInputs,
    buildCleanWasCollectedMap,
    clearDraftInputBufferForCurrentVisit,
    clearEditInputBufferForCurrentVisit,
  ]);
  // Tipos únicos de máquinas
  const getUniqueMachineTypes = (list) => {
    const types = new Set();
    (list || []).forEach((m) => {
      const t = m.MachineType?.Type;
      if (t) types.add(t);
    });
    return Array.from(types);
  };

  // recorre cada localMachineDetails y regresa un arreglo con toda la info calculada para la tabla
  const calculateVisitMachineData = useCallback(() => {
    return visibleLocalMachineDetails.map((machine) => {
      const sourceMachine = isEditMode ? machine : normalizeCreationDraftEditorMachine(machine);
      const keyId = resolveMachineKey(sourceMachine);
      const visitDetail = (visitMachineDetails || []).find(
        (detail) => Number(detail?.LocalMachineId) === keyId
      );
      const mergedMachine = isEditMode
        ? mergeMachineWithVisitDetail(sourceMachine, visitDetail)
        : normalizeCreationDraftEditorMachine(
            mergeMachineWithVisitDetail(sourceMachine, visitDetail)
          );
      if (isPendingInitialValuesNoticeMachine(mergedMachine)) {
        return {
          ...mergedMachine,
          LocalMachineId: keyId,
          IsCollectionNoticeRow: true,
          IsPendingInitialValuesNoticeRow: true,
          CollectionNoticeKind: "pendingInitialValues",
          WasCollected: null,
          note: "",
          Note: null,
          actualCreditsIn: null,
          actualCreditsOut: null,
          netCreditsIn: null,
          netCreditsOut: null,
          moneyIn: null,
          moneyOut: null,
          netMoneyIn: null,
          netMoneyOut: null,
          moneyCollected: null,
          clientAmount: null,
          ownerAmount: null,
          creditsCollected: null,
          HasReset: false,
          PostResetStartIn: null,
          PostResetStartOut: null,
        };
      }

      if (isInsertedInCurrentVisitNoticeMachine(mergedMachine)) {
        return {
          ...mergedMachine,
          LocalMachineId: keyId,
          IsCollectionNoticeRow: true,
          IsInsertedInCurrentVisitNoticeRow: true,
          CollectionNoticeKind: "insertedInCurrentVisit",
          WasCollected: null,
          note: "",
          Note: null,
          actualCreditsIn: null,
          actualCreditsOut: null,
          netCreditsIn: null,
          netCreditsOut: null,
          moneyIn: null,
          moneyOut: null,
          netMoneyIn: null,
          netMoneyOut: null,
          moneyCollected: null,
          clientAmount: null,
          ownerAmount: null,
          creditsCollected: null,
          HasReset: false,
          PostResetStartIn: null,
          PostResetStartOut: null,
        };
      }

      const wasCollected = isEditMode
        ? isVisitMachineCollected(mergedMachine, {
            wasCollectedByLM,
            keyId,
          })
        : resolveCreationDraftWasCollected({
            machine: mergedMachine,
            keyId,
            userInputs,
            wasCollectedByLM,
          });
      const effectiveProfitPercentages = getEffectiveProfitPercentagesForMachine({
        machine: mergedMachine,
        isEditMode,
        activeProfitPercentages: profitPercentages,
      });
      const historicalProfitPercentage = getHistoricalProfitPercentageForEditMachine(mergedMachine);
      const missingHistoricalProfitPercentage =
        isEditMode &&
        !hasRent(mergedMachine) &&
        historicalProfitPercentage?.Id != null &&
        !hasCompleteHistoricalProfitPercentage(historicalProfitPercentage);
      if (!wasCollected) {
        const original = isEditMode
          ? prevRecords.find((r) => Number(r.LocalMachineId) === keyId)
          : null;
        return {
          ...mergedMachine,
          ...COLLECTION_NULL_RESULT,
          LastVisitMachineId: original?.LastVisitMachineId,
          VisitMachineId: resolveVisitMachineId(mergedMachine) ?? original?.VisitMachineId ?? original?.LastVisitMachineId ?? null,
          LocalMachineId: original?.LocalMachineId || keyId,
          MachineTypeId: mergedMachine.MachineType?.Id ?? null,
          FormulaId: null,
          note: userInputs[keyId]?.Note || "",
          // Debe persistir aunque no se recaude.
          LocalMachineProfitPercentageId:
            original?.LocalMachineProfitPercentageId ??
            mergedMachine.LocalMachineProfitPercentageId ??
            historicalProfitPercentage?.Id ??
            null,
          WasCollected: false,
        };
      }
      if (missingHistoricalProfitPercentage) {
        return {
          ...mergedMachine,
          ...COLLECTION_NULL_RESULT,
          LocalMachineId: keyId,
          VisitMachineId: resolveVisitMachineId(mergedMachine),
          LastVisitMachineId: resolveVisitMachineId(mergedMachine),
          MachineTypeId: mergedMachine?.MachineType?.Id ?? mergedMachine?.MachineTypeId ?? null,
          FormulaId: mergedMachine?.Formula?.Id ?? mergedMachine?.FormulaId ?? null,
          note: userInputs[keyId]?.Note || "",
          WasCollected: true,
          HistoricalProfitPercentageError: true,
        };
      }
      const strat = getCollectionStrategy(mergedMachine);
      if (!strat || typeof strat.calculate !== "function") {
        if (process.env.NODE_ENV !== "production") {
          console.error("[VisitMachine] No se encontró estrategia para la máquina", {
            localMachineId: keyId,
            machineType: mergedMachine?.MachineType?.Type ?? mergedMachine?.MachineType ?? null,
            mergedMachine,
          });
        }
        return {
          ...mergedMachine,
          ...COLLECTION_NULL_RESULT,
          LocalMachineId: keyId,
          VisitMachineId: resolveVisitMachineId(mergedMachine),
          LastVisitMachineId: resolveVisitMachineId(mergedMachine),
          MachineTypeId: mergedMachine?.MachineType?.Id ?? mergedMachine?.MachineTypeId ?? null,
          FormulaId: mergedMachine?.Formula?.Id ?? mergedMachine?.FormulaId ?? null,
          note: userInputs[keyId]?.Note || "",
          WasCollected: false,
          StrategyResolutionError: true,
        };
      }
      const calculated = strat.calculate(
        mergedMachine,
        userInputs,
        visitMachineDetails,
        effectiveProfitPercentages,
        isEditMode,
        prevRecords
      );
      return {
        ...mergedMachine,
        ...calculated,
        VisitMachineId: resolveVisitMachineId(mergedMachine),
        LastVisitMachineId: resolveVisitMachineId(mergedMachine) ?? calculated?.LastVisitMachineId,
        LocalMachineId: keyId,
        WasCollected: true,
      };
    });
  }, [
    COLLECTION_NULL_RESULT,
    visibleLocalMachineDetails,
    userInputs,
    visitMachineDetails,
    profitPercentages,
    isEditMode,
    prevRecords,
    wasCollectedByLM,
    isPendingInitialValuesNoticeMachine,
    isInsertedInCurrentVisitNoticeMachine,
    mergeMachineWithVisitDetail,
    normalizeCreationDraftEditorMachine,
  ]);

  const calculatedVisitMachines = useMemo(
    () => calculateVisitMachineData(),
    [calculateVisitMachineData]
  );
  const completedEditMachineList = useMemo(() => {
    return buildCompletedEditMachineListFromPersistedBaseline({
      isEditMode,
      completedEditPersistedBaseline,
    });
  }, [isEditMode, completedEditPersistedBaseline]);
  const selectedEditMachine = useMemo(
    () =>
      (calculatedVisitMachines || []).find(
        (machine) => resolveVisitMachineId(machine) === Number(selectedEditVisitMachineId)
      ) ||
      completedEditMachineList.find(
        (machine) => resolveVisitMachineId(machine) === Number(selectedEditVisitMachineId)
      ) ||
      null,
    [calculatedVisitMachines, completedEditMachineList, selectedEditVisitMachineId]
  );
  const selectedEditMachineType = selectedEditMachine?.MachineType?.Type || selectedEditMachine?.MachineType || "Sin tipo";
  const selectedEditCalculatedMachines = useMemo(
    () => (selectedEditMachine ? [selectedEditMachine] : []),
    [selectedEditMachine]
  );
  const draftMachineList = useMemo(
    () =>
      buildDraftMachineList(
        draftProgress,
        getLocalMachinePresentationDetails(localMachinePresentationDetails, effectiveLocalId)
      ),
    [draftProgress, localMachinePresentationDetails, effectiveLocalId]
  );
  const draftInputDirtyMachineIdSet = useMemo(
    () => new Set((draftInputDirtyMachineIds || []).map((localMachineId) => Number(localMachineId))),
    [draftInputDirtyMachineIds]
  );
  const editInputDirtyMachineIdSet = useMemo(
    () => new Set((editInputDirtyMachineIds || []).map((localMachineId) => Number(localMachineId))),
    [editInputDirtyMachineIds]
  );
  const draftMachineGroups = useMemo(
    () => groupCollectionMachinesLikeLocalMachineIndex(draftMachineList),
    [draftMachineList]
  );
  const completedEditMachineGroups = useMemo(
    () => groupCollectionMachinesLikeLocalMachineIndex(completedEditMachineList),
    [completedEditMachineList]
  );
  const draftPartialTotals = useMemo(
    () => getDraftPartialTotals(draftProgress),
    [draftProgress]
  );
  const canCompleteDraft = useMemo(
    () => canCompleteDraftProgress(draftProgress),
    [draftProgress]
  );
  const draftPersistedMachineCount = useMemo(
    () => getDraftPersistedMachineCount(draftProgress),
    [draftProgress]
  );
  const editDateState = useMemo(
    () => getCompletedEditDateState({
      persistedDate: editPersistedDateInput,
      candidateDate: visitDateInput,
      bounds: collectionEditSafeBounds,
    }),
    [editPersistedDateInput, visitDateInput, collectionEditSafeBounds]
  );
  const canOpenCompletedEditMachineNow = canOpenCompletedEditMachine({
    editDateState,
    saving: saving || isSavingEditDate,
  });
  const selectedDraftMachine = useMemo(() => {
    if (!selectedDraftLocalMachineId) return null;
    const selectedId = Number(selectedDraftLocalMachineId);
    return draftMachineList.find(
      (machine) => Number(machine?.LocalMachineId ?? machine?.Id) === selectedId
    ) || null;
  }, [draftMachineList, selectedDraftLocalMachineId]);
  const selectedDraftMachineType = selectedDraftMachine?.MachineType?.Type ||
    selectedDraftMachine?.MachineType ||
    "Máquina";
  const selectedDraftCalculatedMachines = useMemo(() => {
    if (!selectedDraftLocalMachineId) return [];
    const selectedId = Number(selectedDraftLocalMachineId);
    return calculatedVisitMachines
      .filter((machine) => Number(machine?.LocalMachineId ?? machine?.Id) === selectedId)
      .map(normalizeCreationDraftEditorMachine);
  }, [calculatedVisitMachines, selectedDraftLocalMachineId]);
  const hasPendingDraftDateChange =
    !isEditMode &&
    Boolean(visitDateInput) &&
    Boolean(draftPersistedDateInput) &&
    visitDateInput !== draftPersistedDateInput;

  const getDraftWasCollectedValue = useCallback((localMachineId) => {
    const lmId = Number(localMachineId);
    if (!Number.isFinite(lmId) || lmId <= 0) return true;
    if (typeof wasCollectedByLM?.[lmId] === "boolean") return wasCollectedByLM[lmId];
    if (typeof userInputs?.[lmId]?.WasCollected === "boolean") return userInputs[lmId].WasCollected;
    const machine = draftMachineList.find(
      (item) => Number(item?.LocalMachineId ?? item?.Id) === lmId
    );
    return getDraftMachineDefaultWasCollected(machine);
  }, [wasCollectedByLM, userInputs, draftMachineList]);

  const openDraftMachineEditor = useCallback((localMachineId) => {
    if (hasPendingDraftDateChange) {
      setActionError("Aplicá o cancelá el cambio de fecha antes de cargar una máquina.");
      return;
    }
    const lmId = Number(localMachineId);
    if (!Number.isFinite(lmId) || lmId <= 0) return;
    const defaultWasCollected = getDraftWasCollectedValue(lmId);

    setWasCollectedByLM((prev) => {
      if (typeof prev?.[lmId] === "boolean") return prev;
      return { ...prev, [lmId]: defaultWasCollected };
    });
    setUserInputs((prev) => {
      if (typeof prev?.[lmId]?.WasCollected === "boolean") return prev;
      return {
        ...prev,
        [lmId]: {
          ...prev?.[lmId],
          WasCollected: defaultWasCollected,
        },
      };
    });
    setSelectedDraftLocalMachineId(lmId);
    setActionError("");
  }, [hasPendingDraftDateChange, getDraftWasCollectedValue]);

  const lastRelevantVisitId = isEditMode ? lastLocalVisitBeforeId : lastLocalVisitId;
  const lastRelevantVisitDate = isEditMode ? lastLocalVisitBeforeDate : lastLocalVisitDate;
  const currentCollectionBoundsMinDate = collectionDateBounds?.MinSelectableCollectionDate ?? null;
  const minSelectableCollectionDate = useMemo(() => {
    return [
      currentCollectionBoundsMinDate,
      isEditMode ? collectionEditSafeBounds?.MinSafeEditDate : null,
      firstHistoricalMachineStartDateKey,
      latestActiveMachineStartDateKey,
      !isEditMode ? latestInactiveMachineFinishDateKey : null,
    ]
      .filter(Boolean)
      .reduce((latest, current) => {
        if (!latest) return current;
        return current >= latest ? current : latest;
      }, null);
  }, [
    currentCollectionBoundsMinDate,
    collectionEditSafeBounds?.MinSafeEditDate,
    firstHistoricalMachineStartDateKey,
    latestActiveMachineStartDateKey,
    isEditMode,
    latestInactiveMachineFinishDateKey,
  ]);
  const maxSelectableCollectionDate = useMemo(() => {
    return [
      collectionDateBounds?.MaxSelectableCollectionDate ?? toDateInputValue(new Date()),
      isEditMode ? collectionEditSafeBounds?.MaxSafeEditDate : null,
    ]
      .filter(Boolean)
      .reduce((earliest, current) => {
        if (!earliest) return current;
        return current <= earliest ? current : earliest;
      }, null);
  }, [
    collectionDateBounds?.MaxSelectableCollectionDate,
    collectionEditSafeBounds?.MaxSafeEditDate,
    isEditMode,
  ]);
  const visitDateValidationMessage = useMemo(() => {
    if (!visitDateInput) return "Debes ingresar una fecha válida para la recaudación.";
    if (
      isEditMode &&
      collectionEditSafeBounds?.MinSafeEditDate &&
      visitDateInput < collectionEditSafeBounds.MinSafeEditDate
    ) {
      return (
        collectionEditSafeBounds.MinReason ||
        `La fecha segura minima es ${formatDateKeyDDMMYYYY(
          collectionEditSafeBounds.MinSafeEditDate
        )}.`
      );
    }
    if (
      isEditMode &&
      collectionEditSafeBounds?.MaxSafeEditDate &&
      visitDateInput > collectionEditSafeBounds.MaxSafeEditDate
    ) {
      return (
        collectionEditSafeBounds.MaxReason ||
        `La fecha segura maxima es ${formatDateKeyDDMMYYYY(
          collectionEditSafeBounds.MaxSafeEditDate
        )}.`
      );
    }
    if (
      minSelectableCollectionDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(visitDateInput) &&
      visitDateInput < minSelectableCollectionDate
    ) {
      if (
        !isEditMode &&
        latestInactiveMachineFinishDateKey &&
        minSelectableCollectionDate === latestInactiveMachineFinishDateKey
      ) {
        return "La recaudación no puede realizarse antes del último egreso de una máquina del local.";
      }
      if (
        latestActiveMachineStartDateKey &&
        minSelectableCollectionDate === latestActiveMachineStartDateKey
      ) {
        return "La recaudación no puede realizarse antes del ingreso de la última máquina actualmente instalada en el local.";
      }
      return `La fecha minima permitida es ${formatDateKeyDDMMYYYY(minSelectableCollectionDate)}.`;
    }
    if (
      maxSelectableCollectionDate &&
      /^\d{4}-\d{2}-\d{2}$/.test(visitDateInput) &&
      visitDateInput > maxSelectableCollectionDate
    ) {
      return `La fecha maxima permitida es ${formatDateKeyDDMMYYYY(maxSelectableCollectionDate)}.`;
    }
    return null;
  }, [
    visitDateInput,
    minSelectableCollectionDate,
    maxSelectableCollectionDate,
    collectionEditSafeBounds,
    isEditMode,
    latestInactiveMachineFinishDateKey,
    latestActiveMachineStartDateKey,
  ]);

  const draftDateEditState = useMemo(
    () =>
      getDraftDateEditState({
        isEditMode,
        persistedMachineCount: draftPersistedMachineCount,
        isDateChangeRequested: isDraftDateChangeRequested,
        persistedDate: draftPersistedDateInput,
        candidateDate: visitDateInput,
        validationMessage: visitDateValidationMessage,
        isLoadingBounds: isLoadingCollectionDateBounds,
        hasActiveOperation: saving || isSavingDraftDate || Boolean(draftAction),
        hasOpenMachineEditor: Boolean(selectedDraftLocalMachineId),
      }),
    [
      isEditMode,
      draftPersistedMachineCount,
      isDraftDateChangeRequested,
      draftPersistedDateInput,
      visitDateInput,
      visitDateValidationMessage,
      isLoadingCollectionDateBounds,
      saving,
      isSavingDraftDate,
      draftAction,
      selectedDraftLocalMachineId,
    ]
  );

  // Agrupar por tipo
  const machinesByType = useMemo(() => {
    return calculatedVisitMachines.reduce((g, m) => {
      const type = m.MachineType?.Type || "Otro";
      if (!g[type]) g[type] = [];
      g[type].push(m);
      return g;
    }, {});
  }, [calculatedVisitMachines]);

  // - Separar en no alquiladas / alquiladas por tipo (un solo bloque para alquiladas)
  const { notRentedByType, rentedByType, totalRented } = useMemo(() => {
    const NR = {};
    const R = {};
    let total = 0;

    for (const [type, arr] of Object.entries(machinesByType)) {
      const nr = arr.filter((m) => !hasRent(m));
      const rr = arr.filter((m) => hasRent(m));
      if (nr.length) NR[type] = nr;
      if (rr.length) {
        R[type] = rr;
        total += rr.length;
      }
    }
    return { notRentedByType: NR, rentedByType: R, totalRented: total };
  }, [machinesByType, hasRent]);

  const notRentedEntries = useMemo(
    () => sortTypeEntries(notRentedByType),
    [notRentedByType]
  );

  const rentedEntries = useMemo(
    () => sortTypeEntries(rentedByType),
    [rentedByType]
  );

  const clearDraftMachineDerivedState = useCallback((nextProgress = null) => {
    setSelectedDraftLocalMachineId(null);
    clearDraftInputBufferForCurrentVisit();
    dirtyWasCollectedByLMRef.current = {};
    dirtyDraftInputFieldsRef.current = {};
    userInputsRef.current = {};
    wasCollectedByLMRef.current = {};
    setUserInputs({});
    setWasCollectedByLM({});
    setSettledInputFields({});
    setVisitMachineDetails([]);
    setCounterVerificationContextsByLocalMachineId({});
    const expectedMachines = Array.isArray(nextProgress?.expectedMachines)
      ? nextProgress.expectedMachines
      : [];
    if (expectedMachines.length) {
      setLocalMachineDetails(expectedMachines);
      setMachineTypes(getUniqueMachineTypes(expectedMachines));
    }
  }, [clearDraftInputBufferForCurrentVisit]);

  const { localName: headerLocalName, clientName: headerClientName } =
    resolveCollectionHeaderData({ local });

  // bloque de carga/errores cuando estamos en modo edición y el hook aún está resolviendo
  if (isEditMode && bootLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={3} pb={3}>
          <MDTypography variant="h6">
            Cargando recaudación para editar
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (isEditMode && bootError) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={3} pb={3}>
          <MDTypography variant="h6" color="error">
            {bootError}
          </MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  // ==========================
  // VALIDACIÓN PRE-GUARDADO
  // ==========================
  const saveOrEdit = async (isEdit) => {
    setActionError("");
    const machinesData = calculateVisitMachineData();
    const saveableMachinesData = machinesData.filter(
      (m) => !isInsertedInCurrentVisitNoticeRow(m)
    );
    const visitDateTime = buildVisitDateTime(
      visitDateInput,
      state?.visitDateTime || bootVisitDateTime || null
    );
    const hasValidVisitDateTime =
      typeof visitDateTime === "string" &&
      visitDateTime.trim() !== "" &&
      !Number.isNaN(new Date(visitDateTime).getTime());

    if (!hasValidVisitDateTime) {
      setActionError("Debes ingresar una fecha válida para la recaudación.");
      return;
    }

    if (
      isLoadingCollectionDateBounds ||
      (isEdit && isLoadingCollectionEditSafeBounds)
    ) {
      setActionError("Todavía se están cargando los límites de fecha de la recaudación.");
      return;
    }

    if (visitDateValidationMessage) {
      setActionError(visitDateValidationMessage);
      return;
    }

      // VALIDACIÓN DE REGLAS DE NEGOCIO (antes de construir payload / llamar backend)
    const validationErrors = validateCollectionBeforeSave({
      machines: saveableMachinesData,
      userInputs,
      counterVerificationContextsByLocalMachineId,
      hasOperatorFormula,
    });
    if (validationErrors.length) {
      if (process.env.NODE_ENV !== "production") {
        console.error("[validateBeforeSave] Errores:", validationErrors);
      }
      setActionError(validationErrors[0]);
      return;
    }

    // Paso 1: primer intento con lo que ya tenemos
    let { payload, missing } = buildCollectionPayload({
      machines: saveableMachinesData,
      visitWithCollectionId,
      isEdit,
      prevRecords,
      userInputs,
      counterVerificationContextsByLocalMachineId,
    });

    if (!payload.length) {
      setActionError("No hay máquinas recaudables para la fecha seleccionada.");
      return;
    }

    // Paso 2: si faltan, intentamos completar con /details/:visitId (una sola llamada)
    if (isEdit && missing.length) {
      try {
        const det = await VisitTransactionService.getVisitTransactionDetails(
          visitWithCollectionId,
          { localId: local?.Id ?? null }
        );
        const mapLast = new Map(
          (det?.machines || []).map((r) => [
            Number(r.LocalMachineId),
            Number(r.Id),
          ]) // LocalMachineId -> VisitMachine.Id
        );
        missing = [];
        payload = payload.map((row) => {
          if (row.LastVisitMachineId) return row;
          const lmId = row.LocalMachineId;
          const lastId = mapLast.get(Number(lmId));
          if (!lastId) missing.push({ LocalMachineId: lmId });
          return { ...row, LastVisitMachineId: lastId || row.LastVisitMachineId };
        });
      } catch (e) {
        if (process.env.NODE_ENV !== "production")
          console.error("Fallback /details/ failed", e);
      }
    }

    if (isEdit && missing.length) {
      setActionError(
        `No se pudo resolver el Id de VisitMachine para ${missing.length} máquina(s). Cerrá y reabrí "Editar" desde el detalle, o recargá la página.`
      );
      return; // no seguimos para evitar updates nulos
    }

    try {
      setSaving(true);
      setHistoricalEditDateError(null);
      const result = isEdit
        ? await VisitTransactionService.updateVisitTransactionFull(visitWithCollectionId, {
            visitDateTime,
            visitMachines: payload,
          })
        : await VisitTransactionService.finalizeVisitDraft(visitWithCollectionId, {
            draftKind: "collection",
            visitDateTime,
            visitMachines: payload,
          });
      if (!result) {
        throw new Error("Operacion rechazada por el servidor");
      }
      notifySuccess(
        isEdit ? "Recaudación actualizada" : "Recaudación guardada"
      );
      const detailLocalId = Number(local?.Id ?? state?.localId ?? id);
      const detailPath = Number.isFinite(detailLocalId) && detailLocalId > 0
        ? `/local/${detailLocalId}/recaudaciones/${visitWithCollectionId}`
        : `/visit-details/${visitWithCollectionId}`;
      navigate(detailPath, {
        replace: true,
        state: {
          local,
          returnTo: state?.returnTo || null,
        },
      });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        console.error(e);
      }
      const errorInfo = getVisitTransactionSaveErrorInfo(e);
      if (isEdit && COLLECTION_EDIT_HISTORICAL_ERROR_CODES.has(errorInfo.code)) {
        setHistoricalEditDateError(errorInfo.message);
      } else {
        notifyError(errorInfo.message || `Error ${isEdit ? "actualizando" : "guardando"} recaudación`);
      }
    } finally {
      setSaving(false);
    }
  };

  const refreshEditDateBounds = async () => {
    if (!effectiveEditVisitId) return null;
    setIsLoadingCollectionEditSafeBounds(true);
    setCollectionEditSafeBoundsError(null);
    try {
      const bounds = await VisitTransactionService.getCollectionEditSafeDateBounds(effectiveEditVisitId);
      setCollectionEditSafeBounds(bounds);
      return bounds;
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      setCollectionEditSafeBounds(null);
      setCollectionEditSafeBoundsError(
        errorInfo.message || "No se pudo calcular el rango histórico seguro."
      );
      return null;
    } finally {
      setIsLoadingCollectionEditSafeBounds(false);
    }
  };

  const closeSelectedCompletedEditMachine = () => {
    if (editMachineSavingRef.current || editDateSavingRef.current || savingRef.current) return;
    setSelectedEditVisitMachineId(null);
  };

  const saveSelectedCompletedEditMachine = async () => {
    if (editMachineSavingRef.current || editDateSavingRef.current || savingRef.current) return;
    setActionError("");
    const visitMachineId = resolveVisitMachineId(selectedEditMachine);
    if (!visitMachineId || !selectedEditMachine) {
      setActionError("Seleccioná una máquina para corregir.");
      return;
    }

    editMachineSavingRef.current = true;
    try {
      setSaving(true);
      const machineToSave = {
        ...selectedEditMachine,
        WasCollected: isVisitMachineCollected(selectedEditMachine, {
          wasCollectedByLM,
          keyId: Number(selectedEditMachine.LocalMachineId),
        }),
      };
      if (
        hasIncompleteIndividualSlotEntryExit(
          machineToSave,
          userInputs,
          counterVerificationContextsByLocalMachineId
        )
      ) {
        setOpenIncompleteSlotDialog(true);
        return;
      }
      const historicalPercentage = getHistoricalProfitPercentageForEditMachine(machineToSave);
      if (
        !hasRent(machineToSave) &&
        historicalPercentage?.Id != null &&
        !hasCompleteHistoricalProfitPercentage(historicalPercentage)
      ) {
        setActionError("No se puede guardar: falta el porcentaje histórico de esta máquina.");
        return;
      }
      if (machineToSave.HistoricalProfitPercentageError) {
        setActionError("No se puede guardar: falta el porcentaje histórico de esta máquina.");
        return;
      }
      const validationErrors = validateCollectionBeforeSave({
        machines: [machineToSave],
        userInputs,
        counterVerificationContextsByLocalMachineId,
        hasOperatorFormula,
      });
      if (validationErrors.length) {
        setActionError(validationErrors[0]);
        return;
      }
      const { payload } = buildCollectionPayload({
        machines: [machineToSave],
        visitWithCollectionId,
        isEdit: true,
        prevRecords: [machineToSave],
        userInputs,
        counterVerificationContextsByLocalMachineId,
      });
      const machinePayload = payload?.[0];
      if (!machinePayload) {
        setActionError("No hay datos para guardar en la máquina seleccionada.");
        return;
      }

      const result = await VisitTransactionService.patchCompletedVisitMachine(
        visitWithCollectionId,
        visitMachineId,
        machinePayload
      );
      const persistedBase = completedEditPersistedBaseline.find(
        (machine) => resolveVisitMachineId(machine) === visitMachineId
      );
      const resultHasCounterVerification =
        Object.prototype.hasOwnProperty.call(result || {}, "counterVerification") ||
        Object.prototype.hasOwnProperty.call(result?.visitMachine || {}, "CounterVerification");
      const nextCounterVerification = resultHasCounterVerification
        ? (result?.counterVerification ?? result?.visitMachine?.CounterVerification ?? null)
        : (persistedBase?.CounterVerification ?? selectedEditMachine?.CounterVerification ?? null);
      const updatedMachine = normalizeCompletedEditDetailMachine({
        ...(persistedBase || selectedEditMachine),
        ...selectedEditMachine,
        ...(result?.visitMachine || {}),
        CounterVerification: nextCounterVerification,
      });
      setEditMachineRows((prev) => replaceEditedVisitMachine(prev, updatedMachine));
      setUserInputs((prev) => ({
        ...prev,
        ...buildInputsFromMachines([updatedMachine]),
      }));
      setWasCollectedByLM((prev) => ({
        ...prev,
        [Number(updatedMachine.LocalMachineId)]: updatedMachine.WasCollected !== false,
      }));
      setEditTotals(result?.totals || result?.visitWithCollection || null);
      clearEditInputBufferMachineForCurrentVisit(updatedMachine.LocalMachineId);
      setSelectedEditVisitMachineId(null);
      setEditSuccessMessageKey((prev) => prev + 1);
      setEditSuccessMessage("Corrección guardada correctamente");
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      if (errorInfo.code === "ONLY_LATEST_VISIT_EDITABLE") {
        notifyError(errorInfo.message);
        const detailLocalId = Number(local?.Id ?? state?.localId ?? id);
        const detailPath = Number.isFinite(detailLocalId) && detailLocalId > 0
          ? `/local/${detailLocalId}/recaudaciones/${visitWithCollectionId}`
          : `/visit-details/${visitWithCollectionId}`;
        navigate(detailPath, { replace: true, state: { local, returnTo: state?.returnTo || null } });
        return;
      }
      notifyError(errorInfo.message || "Error actualizando la máquina.");
    } finally {
      setSaving(false);
      editMachineSavingRef.current = false;
    }
  };

  const discardSelectedCompletedEditMachine = () => {
    if (editMachineSavingRef.current || editDateSavingRef.current || savingRef.current) return;
    const localMachineId = Number(selectedEditMachine?.LocalMachineId ?? selectedEditMachine?.Id);
    if (!Number.isFinite(localMachineId) || localMachineId <= 0) return;
    const persistedMachine = completedEditPersistedBaseline.find(
      (machine) => Number(machine?.LocalMachineId ?? machine?.Id) === localMachineId
    );
    if (!persistedMachine) return;

    const persistedInputs = buildInputsFromMachines([persistedMachine]);
    const persistedMachineInputs =
      persistedInputs?.[localMachineId] ?? persistedInputs?.[String(localMachineId)] ?? {};
    const persistedWasCollected = isPersistedCollectedMachine(persistedMachine);
    setUserInputs((prev) => {
      const nextInputs = {
        ...prev,
        [localMachineId]: {
          ...persistedMachineInputs,
          WasCollected: persistedWasCollected,
        },
      };
      userInputsRef.current = nextInputs;
      return nextInputs;
    });
    setWasCollectedByLM((prev) => {
      const nextMap = { ...prev, [localMachineId]: persistedWasCollected };
      wasCollectedByLMRef.current = nextMap;
      return nextMap;
    });
    clearEditInputBufferMachineForCurrentVisit(localMachineId);
    setSettledInputFields((prev) => {
      const next = { ...prev };
      delete next[localMachineId];
      return next;
    });
    setResetVersion((prev) => prev + 1);
    setOpenDiscardEditMachineDialog(false);
  };

  const saveCompletedVisitDate = async () => {
    if (editDateSavingRef.current || editMachineSavingRef.current || savingRef.current) return;
    if (!editDateState.canSave) {
      setActionError("La fecha no está lista para guardarse.");
      return;
    }

    editDateSavingRef.current = true;
    setActionError("");
    try {
      setIsSavingEditDate(true);
      setHistoricalEditDateError(null);
      const result = await VisitTransactionService.patchCompletedVisitDate(
        visitWithCollectionId,
        visitDateInput
      );
      const nextDate = result?.date || visitDateInput;
      setVisitDateInput(nextDate);
      setEditPersistedDateInput(nextDate);
      await refreshEditDateBounds();
      notifySuccess(result?.idempotent ? "Fecha sin cambios" : "Fecha actualizada");
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      setVisitDateInput(editPersistedDateInput);
      setHistoricalEditDateError(errorInfo.message);
      if (errorInfo.code === "ONLY_LATEST_VISIT_EDITABLE") {
        notifyError(errorInfo.message);
        const detailLocalId = Number(local?.Id ?? state?.localId ?? id);
        const detailPath = Number.isFinite(detailLocalId) && detailLocalId > 0
          ? `/local/${detailLocalId}/recaudaciones/${visitWithCollectionId}`
          : `/visit-details/${visitWithCollectionId}`;
        navigate(detailPath, { replace: true, state: { local, returnTo: state?.returnTo || null } });
        return;
      }
    } finally {
      setIsSavingEditDate(false);
      editDateSavingRef.current = false;
    }
  };

  const applyDraftDateChange = async ({ resetPersistedMachines = false } = {}) => {
    if (isEditMode) return;
    if (savingRef.current || draftDateSavingRef.current || draftActionRef.current) return;
    if (!visitWithCollectionId) {
      notifyError("No se pudo resolver el borrador de recaudación.");
      return;
    }
    if (!draftDateEditState.canApply) {
      if (visitDateValidationMessage) setActionError(visitDateValidationMessage);
      return;
    }

    draftDateSavingRef.current = true;
    setIsSavingDraftDate(true);
    try {
      const result = await VisitTransactionService.patchDraftDate(visitWithCollectionId, {
        date: visitDateInput,
        resetPersistedMachines,
      });
      const nextProgress = result?.progress || null;
      if (nextProgress) {
        setDraftProgress(nextProgress);
      }
      const nextDate =
        result?.date ||
        toDateInputValue(result?.visitDateTime || nextProgress?.visit?.DateTime || visitDateInput);
      setVisitDateInput(nextDate);
      clearDraftMachineDerivedState(nextProgress);
      setOpenDraftDateResetDialog(false);
      setIsDraftDateChangeRequested(false);
      notifySuccess("Fecha del borrador actualizada");
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      if (errorInfo.code === "COLLECTION_DRAFT_DATE_CHANGE_REQUIRES_RESET") {
        setOpenDraftDateResetDialog(true);
        return;
      }
      setVisitDateInput(draftPersistedDateInput);
      setIsDraftDateChangeRequested(false);
      setOpenDraftDateResetDialog(false);
      notifyError(errorInfo.message || "No se pudo actualizar la fecha del borrador.");
    } finally {
      setIsSavingDraftDate(false);
      draftDateSavingRef.current = false;
    }
  };

  const saveSelectedDraftMachine = async ({ goToNextPending = false } = {}) => {
    if (savingRef.current || draftDateSavingRef.current || draftActionRef.current) return;
    setActionError("");
    savingRef.current = true;
    const localMachineId = Number(selectedDraftLocalMachineId);
    if (!Number.isFinite(localMachineId) || localMachineId <= 0) {
      savingRef.current = false;
      setActionError("Seleccioná una máquina para guardar.");
      return;
    }

    const visitDateTime = buildVisitDateTime(
      visitDateInput,
      draftProgress?.visit?.DateTime || state?.visitDateTime || bootVisitDateTime || null
    );
    const hasValidVisitDateTime =
      typeof visitDateTime === "string" &&
      visitDateTime.trim() !== "" &&
      !Number.isNaN(new Date(visitDateTime).getTime());

    if (!hasValidVisitDateTime) {
      savingRef.current = false;
      setActionError("Debes ingresar una fecha válida para la recaudación.");
      return;
    }
    if (isLoadingCollectionDateBounds) {
      savingRef.current = false;
      setActionError("Todavía se están cargando los límites de fecha de la recaudación.");
      return;
    }
    if (visitDateValidationMessage) {
      savingRef.current = false;
      setActionError(visitDateValidationMessage);
      return;
    }

    const machinesData = calculateVisitMachineData();
    const calculatedMachineToSave = machinesData.find(
      (machine) => Number(machine?.LocalMachineId ?? machine?.Id) === localMachineId
    );
    if (!calculatedMachineToSave || isInsertedInCurrentVisitNoticeRow(calculatedMachineToSave)) {
      savingRef.current = false;
      setActionError("No se pudo resolver la máquina seleccionada.");
      return;
    }
    const wasCollectedForSave = getDraftWasCollectedValue(localMachineId);
    const machineToSave = {
      ...calculatedMachineToSave,
      WasCollected: wasCollectedForSave,
    };

    if (
      hasIncompleteIndividualSlotEntryExit(
        machineToSave,
        userInputs,
        counterVerificationContextsByLocalMachineId
      )
    ) {
      savingRef.current = false;
      setOpenIncompleteSlotDialog(true);
      return;
    }

    const validationErrors = validateCollectionBeforeSave({
      machines: [machineToSave],
      userInputs,
      counterVerificationContextsByLocalMachineId,
      hasOperatorFormula,
    });
    if (validationErrors.length) {
      savingRef.current = false;
      setActionError(validationErrors[0]);
      return;
    }

    const { payload } = buildCollectionPayload({
      machines: [machineToSave],
      visitWithCollectionId,
      isEdit: false,
      userInputs,
      counterVerificationContextsByLocalMachineId,
    });
    const machinePayload = payload?.[0];
    if (!machinePayload) {
      savingRef.current = false;
      setActionError("No hay datos para guardar en la máquina seleccionada.");
      return;
    }

    try {
      setSaving(true);
      const result = await VisitTransactionService.saveDraftMachine(
        visitWithCollectionId,
        localMachineId,
        machinePayload
      );
      if (dirtyWasCollectedByLMRef.current?.[localMachineId]) {
        const { [localMachineId]: _savedWasCollected, ...restDirtyWasCollected } =
          dirtyWasCollectedByLMRef.current;
        dirtyWasCollectedByLMRef.current = restDirtyWasCollected;
      }
      clearDraftInputBufferMachineForCurrentVisit(localMachineId);
      const nextProgress = result?.progress || await refreshDraftProgress();
      if (result?.progress) setDraftProgress(result.progress);
      notifySuccess("Máquina guardada");

      if (goToNextPending) {
        const nextId = getNextPendingLocalMachineId(
          buildDraftMachineList(
            nextProgress,
            getLocalMachinePresentationDetails(localMachinePresentationDetails, effectiveLocalId)
          ),
          localMachineId
        );
        if (nextId) {
          openDraftMachineEditor(nextId);
        } else {
          setSelectedDraftLocalMachineId(null);
        }
      } else {
        setSelectedDraftLocalMachineId(null);
      }
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      if (
        errorInfo.code === "COLLECTION_DRAFT_NOT_FOUND" ||
        errorInfo.code === "COLLECTION_DRAFT_ALREADY_COMPLETE"
      ) {
        markDraftTerminalAndClear();
        const destination = getVisitDraftTerminalDestination({
          outcome: errorInfo.code === "COLLECTION_DRAFT_ALREADY_COMPLETE" ? "alreadyComplete" : "notFound",
          localId: local?.Id ?? state?.localId ?? id,
          visitId: visitWithCollectionId,
        });
        notifyError(errorInfo.message || "La recaudación ya no está disponible.");
        navigate(destination.path, {
          replace: true,
          state: { local, returnTo: state?.returnTo || null },
        });
        return;
      }
      notifyError(errorInfo.message || "Error guardando la máquina.");
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const completeCurrentDraft = async () => {
    if (!tryBeginDraftAction("complete")) return;
    if (!canCompleteDraft) {
      finishDraftAction();
      setActionError("Todavía hay máquinas pendientes.");
      return;
    }

    try {
      const result = await VisitTransactionService.completeDraft(visitWithCollectionId);
      markDraftTerminalAndClear();
      notifySuccess("Recaudación finalizada");
      const destination = getVisitDraftTerminalDestination({
        outcome: result?.alreadyComplete ? "alreadyComplete" : "complete",
        localId: local?.Id ?? state?.localId ?? id,
        visitId: visitWithCollectionId,
        result,
      });
      navigate(destination.path, {
        replace: true,
        state: {
          local,
          returnTo: state?.returnTo || null,
        },
      });
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      if (errorInfo.code === "COLLECTION_DRAFT_ALREADY_COMPLETE") {
        markDraftTerminalAndClear();
        const destination = getVisitDraftTerminalDestination({
          outcome: "alreadyComplete",
          localId: local?.Id ?? state?.localId ?? id,
          visitId: visitWithCollectionId,
        });
        navigate(destination.path, {
          replace: true,
          state: {
            local,
            returnTo: state?.returnTo || null,
          },
        });
        return;
      }
      notifyError(errorInfo.message || "Error finalizando la recaudación.");
      await refreshDraftProgress();
    } finally {
      finishDraftAction();
    }
  };

  const cancelCurrentDraft = async () => {
    if (!tryBeginDraftAction("cancel")) return;
    try {
      await VisitTransactionService.deleteDraft(visitWithCollectionId);
      markDraftTerminalAndClear();
      notifySuccess("Recaudación cancelada");
      setOpenCancelDraftDialog(false);
      const destination = getVisitDraftTerminalDestination({
        outcome: "cancel",
        localId: local?.Id ?? state?.localId ?? id,
        visitId: visitWithCollectionId,
      });
      navigate(destination.path, {
        replace: true,
        state: { local },
      });
    } catch (error) {
      const errorInfo = getVisitTransactionSaveErrorInfo(error);
      if (errorInfo.code === "COLLECTION_DRAFT_NOT_FOUND") {
        markDraftTerminalAndClear();
        notifyError("El borrador ya no existe. Se limpió la referencia local.");
        setOpenCancelDraftDialog(false);
        const destination = getVisitDraftTerminalDestination({
          outcome: "notFound",
          localId: local?.Id ?? state?.localId ?? id,
          visitId: visitWithCollectionId,
        });
        navigate(destination.path, {
          replace: true,
          state: { local },
        });
        return;
      }
      if (
        errorInfo.code === "COLLECTION_DRAFT_CANNOT_CANCEL_COMPLETE" ||
        errorInfo.code === "COLLECTION_DRAFT_ALREADY_COMPLETE"
      ) {
        markDraftTerminalAndClear();
        setOpenCancelDraftDialog(false);
        notifyError("La recaudación ya fue finalizada.");
        const destination = getVisitDraftTerminalDestination({
          outcome: "alreadyComplete",
          localId: local?.Id ?? state?.localId ?? id,
          visitId: visitWithCollectionId,
        });
        navigate(destination.path, {
          replace: true,
          state: {
            local,
            returnTo: state?.returnTo || null,
          },
        });
        return;
      }
      notifyError(errorInfo.message || "Error cancelando la recaudación.");
    } finally {
      finishDraftAction();
    }
  };

  const renderDraftMachineCard = (machine) => {
    const localMachineId = Number(machine?.LocalMachineId ?? machine?.Id);
    const status = normalizeDraftMachineState(machine);
    const machineName = machine?.Machine?.Name ?? machine?.MachineName ?? machine?.Name ?? "-";
    const displayNumber = machine?.LocalDisplayNumber ?? machine?.Machine?.LocalDisplayNumber ?? null;
    const listPresentation = getCollectionGeneralListPresentation(machine, {
      moneyFormat: moneyDyn.format,
    });
    const persistedMoneyCollected = getDraftPersistedMoneyCollected(machine);
    const hasUnsavedLocalChanges = draftInputDirtyMachineIdSet.has(localMachineId);

    return (
      <MDBox
        key={localMachineId}
        className="collection-machine-card"
        mb={1.75}
        p={1.75}
        borderRadius="md"
        sx={{
          border: "1px solid",
          borderColor: status === "pending" ? "grey.300" : "success.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2.25,
          flexWrap: "wrap",
          backgroundColor: "#fff",
        }}
      >
        <MDBox sx={{ minWidth: 260, flex: 1 }}>
          <Box className="collection-machine-chips" sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            {listPresentation.showLocalDisplayNumber && displayNumber && (
              <Chip size="small" label={String(displayNumber)} />
            )}
            <Chip
              size="small"
              color={
                status === "pending"
                  ? "default"
                  : status === "not_collected"
                  ? "warning"
                  : "success"
              }
              label={getDraftMachineStatusLabel(machine)}
            />
            {status === "saved" && persistedMoneyCollected !== null && (
              <Chip
                size="small"
                label={moneyDyn.format(persistedMoneyCollected)}
                sx={
                  isNegativeCollectionAmount(persistedMoneyCollected)
                    ? { "& .MuiChip-label": { color: "error.main" } }
                    : undefined
                }
              />
            )}
            {hasUnsavedLocalChanges && (
              <Chip size="small" color="info" variant="outlined" label="Cambios sin guardar" />
            )}
          </Box>
          <MDTypography className="collection-machine-name" variant="button" fontWeight="medium" display="block" sx={{ mt: 1 }}>
            {machineName}
          </MDTypography>
          <MDTypography className="collection-machine-secondary" variant="caption" color="text" display="block">
            {listPresentation.secondaryText}
          </MDTypography>
        </MDBox>
        <Button
          className="collection-open-button"
          variant="outlined"
          color="info"
          sx={secondaryOutlinedButtonSx}
          disabled={saving || isSavingDraftDate || Boolean(draftAction) || hasPendingDraftDateChange}
          onClick={() => openDraftMachineEditor(localMachineId)}
        >
          Abrir
        </Button>
      </MDBox>
    );
  };

  const renderCompletedEditMachineCard = (machine) => {
    const visitMachineId = resolveVisitMachineId(machine);
    const localMachineId = Number(machine?.LocalMachineId ?? machine?.Id);
    const machineName = machine?.Machine?.Name ?? machine?.MachineName ?? machine?.Name ?? "-";
    const displayNumber = machine?.LocalDisplayNumber ?? machine?.Machine?.LocalDisplayNumber ?? null;
    const listPresentation = getCollectionGeneralListPresentation(machine, {
      moneyFormat: moneyDyn.format,
    });
    const persistedMoneyCollected = machine?.moneyCollected ?? machine?.MoneyCollected ?? 0;
    const hasUnsavedLocalChanges = editInputDirtyMachineIdSet.has(localMachineId);

    return (
      <MDBox
        key={visitMachineId}
        className="collection-machine-card"
        mb={1.75}
        p={1.75}
        borderRadius="md"
        sx={{
          border: "1px solid",
          borderColor: machine?.WasCollected === false ? "warning.main" : "success.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2.25,
          flexWrap: "wrap",
        }}
      >
        <MDBox sx={{ minWidth: 260, flex: 1 }}>
          <Box className="collection-machine-chips" sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            {listPresentation.showLocalDisplayNumber && displayNumber && (
              <Chip size="small" label={String(displayNumber)} />
            )}
            <Chip
              size="small"
              color={machine?.WasCollected === false ? "warning" : "success"}
              label={getCompletedEditMachineStatusLabel(machine)}
            />
            {hasUnsavedLocalChanges && (
              <Chip size="small" color="info" variant="outlined" label="Cambios sin guardar" />
            )}
            {machine?.WasCollected !== false && (
              <Chip
                size="small"
                label={moneyDyn.format(persistedMoneyCollected)}
                sx={
                  isNegativeCollectionAmount(persistedMoneyCollected)
                    ? { "& .MuiChip-label": { color: "error.main" } }
                    : undefined
                }
              />
            )}
          </Box>
          <MDTypography className="collection-machine-name" variant="button" fontWeight="medium" display="block" sx={{ mt: 1 }}>
            {machineName}
          </MDTypography>
          <MDTypography className="collection-machine-secondary" variant="caption" color="text" display="block">
            {listPresentation.secondaryText}
          </MDTypography>
        </MDBox>
        <Button
          className="collection-open-button"
          variant="outlined"
          color="info"
          sx={secondaryOutlinedButtonSx}
          disabled={!canOpenCompletedEditMachineNow}
          onClick={() => setSelectedEditVisitMachineId(visitMachineId)}
        >
          Abrir
        </Button>
      </MDBox>
    );
  };

  const renderGroupSubtotal = (type, machines, draft) => {
    const subtotal = getCollectionGroupSubtotal(machines, { draft });
    const pluralType = machineTypeLabel(type, 2, { capitalize: false }).toLowerCase();
    const percentageLabel = (role, percentage) => {
      if (subtotal.percentageStatus === "variable") return `${role} porcentajes variables`;
      if (subtotal.percentageStatus === "uniform") {
        return `${role} ${new Intl.NumberFormat("es-UY", { maximumFractionDigits: 2 }).format(percentage)}%`;
      }
      return role;
    };
    return (
      <MDBox className="collection-group-subtotal" sx={{ mt: 0.5, pt: 2, pb: 0.5, borderTop: "1px solid", borderColor: "grey.300", minWidth: 0, width: "100%" }}>
        <MDTypography className="collection-group-subtotal-line" variant="button" fontWeight="medium" display="block" sx={{ whiteSpace: "normal", overflowWrap: "break-word" }}>
          Total {pluralType} {moneyDyn.format(subtotal.TotalAmount)}
        </MDTypography>
        <MDTypography className="collection-group-subtotal-line" variant="caption" color="text" component="div" sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1, rowGap: 0.5, lineHeight: 1.4, overflowWrap: "anywhere", "@media (max-width: 600px)": { flexDirection: "column", alignItems: "stretch" } }}>
          <span>{percentageLabel("Cliente", subtotal.ClientPercentage)} {moneyDyn.format(subtotal.TotalClientAmount)}</span>
          <Box component="span" aria-hidden="true" sx={{ "@media (max-width: 600px)": { display: "none" } }}>·</Box>
          <span>{percentageLabel("Dueño", subtotal.OwnerPercentage)} {moneyDyn.format(subtotal.TotalOwnerAmount)}</span>
        </MDTypography>
      </MDBox>
    );
  };

  const renderDraftMachineGroups = (entries, { rented = false } = {}) =>
    entries.map(([type, machines]) => {
      const gender = machineTypeGender(type);
      const rentedChip = rented ? (gender === "m" ? "Alquilados" : "Alquiladas") : null;
      return (
        <TypeBlock
          key={`${rented ? "rented" : "not-rented"}-${type}`}
          type={type}
          header={
            <TypeChipsHeader
              type={type}
              typeLabel={machineTypeLabel(type, machines.length)}
              rightChip={rentedChip}
            />
          }
        >
          {machines.map(renderDraftMachineCard)}
          {renderGroupSubtotal(type, machines, true)}
        </TypeBlock>
      );
    });

  const renderCompletedEditMachineGroups = (entries, { rented = false } = {}) =>
    entries.map(([type, machines]) => {
      const gender = machineTypeGender(type);
      const rentedChip = rented ? (gender === "m" ? "Alquilados" : "Alquiladas") : null;
      return (
        <TypeBlock
          key={`edit-${rented ? "rented" : "not-rented"}-${type}`}
          type={type}
          header={
            <TypeChipsHeader
              type={type}
              typeLabel={machineTypeLabel(type, machines.length)}
              rightChip={rentedChip}
            />
          }
        >
          {machines.map(renderCompletedEditMachineCard)}
          {renderGroupSubtotal(type, machines, false)}
        </TypeBlock>
      );
    });

  const draftProgressAndActionsBlock = (
    <MDBox
      className="collection-totals-block"
      mt={2}
      mb={2}
      p={2}
      borderRadius="md"
      sx={{
        border: "1px solid",
        borderColor: "grey.300",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <MDBox>
        <MDTypography variant="button" fontWeight="medium" display="block">
          Total: {moneyDyn.format(draftPartialTotals.TotalAmount)}
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block">
          Cliente: {moneyDyn.format(draftPartialTotals.TotalClientAmount)} · Empresa:{" "}
          {moneyDyn.format(draftPartialTotals.TotalOwnerAmount)}
        </MDTypography>
      </MDBox>
      <Box className="collection-draft-footer-actions" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <Button
          className="collection-draft-footer-button"
          variant="outlined"
          color="error"
          sx={dangerOutlinedButtonSx}
          onClick={() => setOpenCancelDraftDialog(true)}
          disabled={saving || isSavingDraftDate || Boolean(draftAction)}
        >
          Cancelar
        </Button>
        <Button
          className="collection-draft-footer-button"
          variant="contained"
          color="success"
          sx={successContainedButtonSx}
          onClick={completeCurrentDraft}
          disabled={!canCompleteDraft || saving || isSavingDraftDate || Boolean(draftAction)}
        >
          {draftAction === "complete" ? "Guardando..." : "Guardar"}
        </Button>
      </Box>
    </MDBox>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <TypeBlocksGlobalStyles rootId="#visit-edit" />
      <GlobalStyles styles={collectionGeneralTypographyStyles} />

      <MDBox id="visit-edit" pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                className="collection-page-header"
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={
                  isEditMode
                    ? {
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
                        columnGap: 2,
                        "& > *": { minWidth: 0 },
                        "@media (max-width: 600px)": {
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "0.65rem",
                        },
                      }
                    : undefined
                }
              >
                {/* IZQUIERDA: ti­tulo + local */}
                <MDTypography variant="h6" color="white">
                  {isEditMode ? "Editar recaudación" : "Recaudación de local"}: {headerLocalName}.
                </MDTypography>

                {/* CENTRO: cliente + fecha */}
                <MDBox sx={{ textAlign: "center" }}>
                  <MDTypography variant="button" color="white" fontWeight="regular">
                    Cliente: {headerClientName}.
                  </MDTypography>
                </MDBox>

                {!isEditMode && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      color="inherit"
                      onClick={handleOpenBackDialog}
                      disabled={saving || isSavingDraftDate || Boolean(draftAction)}
                    >
                      Volver al local
                    </Button>
                  </Box>
                )}
              </MDBox>

              <AppActionDialog
                open={openBackDialog}
                onClose={() => {
                  if (!saving && !isSavingEditDate && !isSavingDraftDate && !draftAction) handleCloseBackDialog();
                }}
                onConfirm={handleConfirmBackToLocal}
                title={isEditMode ? "Salir de edición" : "La recaudación quedará como borrador"}
                description={
                  isEditMode
                    ? "¿Querés descartar los cambios sin guardar y salir? Las correcciones ya guardadas se conservarán"
                    : "Podrás retomarla más tarde. Los cambios que todavía no guardaste podrían perderse."
                }
                cancelLabel={isEditMode ? "Seguir editando" : "Seguir en la recaudación"}
                confirmLabel={isEditMode ? "Descartar y salir" : "Volver al local"}
                confirmColor={isEditMode ? "error" : "primary"}
                busy={saving || isSavingEditDate || isSavingDraftDate || Boolean(draftAction)}
              />

              <MDBox mx={2} pt={3}>
                <MDBox
                  className="collection-date-panel"
                  mb={3}
                  p={2}
                  borderRadius="lg"
                  sx={{
                    border: "1px solid",
                    borderColor: "grey.300",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <FormLabel required htmlFor="collection-date" className="collection-date-label" sx={{ fontWeight: 500 }}>
                    Fecha
                  </FormLabel>
                  <TextField
                    id="collection-date"
                    type="date"
                    required
                    size="small"
                    value={visitDateInput}
                    onChange={(e) => {
                      setVisitDateInput(e.target.value);
                      setHistoricalEditDateError(null);
                      setActionError("");
                    }}
                    disabled={
                      isEditMode
                        ? isCollectionDirty || saving || isSavingEditDate || Boolean(selectedEditVisitMachineId)
                        : !draftDateEditState.canEditDate
                    }
                    error={Boolean(visitDateValidationMessage)}
                    helperText={visitDateValidationMessage || " "}
                    inputProps={{
                      min: minSelectableCollectionDate || undefined,
                      max: maxSelectableCollectionDate || undefined,
                    }}
                    sx={{ minWidth: 220 }}
                  />
                  {!isEditMode && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {draftDateEditState.showModifyButton && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          sx={secondaryOutlinedButtonSx}
                          disabled={saving || isSavingDraftDate || Boolean(draftAction) || Boolean(selectedDraftLocalMachineId)}
                          onClick={() => setIsDraftDateChangeRequested(true)}
                        >
                          Modificar fecha
                        </Button>
                      )}
                      {draftDateEditState.canEditDate && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          sx={successContainedButtonSx}
                          disabled={!draftDateEditState.canApply}
                          onClick={() => {
                            if (draftDateEditState.requiresResetConfirmation) {
                              setOpenDraftDateResetDialog(true);
                              return;
                            }
                            applyDraftDateChange({ resetPersistedMachines: false });
                          }}
                        >
                          {isSavingDraftDate
                            ? "Aplicando..."
                            : draftDateEditState.requiresResetConfirmation
                            ? "Aplicar nueva fecha"
                            : "Aplicar fecha"}
                        </Button>
                      )}
                      {draftDateEditState.showCancelChangeButton && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={secondaryOutlinedButtonSx}
                          disabled={saving || isSavingDraftDate || Boolean(draftAction)}
                          onClick={() => {
                            setVisitDateInput(draftPersistedDateInput);
                            setIsDraftDateChangeRequested(false);
                          }}
                        >
                          Cancelar cambio
                        </Button>
                      )}
                    </Box>
                  )}
                  {isEditMode && (
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {editDateState.changed && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          sx={secondaryOutlinedButtonSx}
                          disabled={saving || isSavingEditDate || Boolean(selectedEditVisitMachineId)}
                          onClick={() => setVisitDateInput(editPersistedDateInput)}
                        >
                          Restablecer fecha
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="contained"
                        color="info"
                        onClick={saveCompletedVisitDate}
                        disabled={
                          !editDateState.canSave ||
                          saving ||
                          isSavingEditDate ||
                          Boolean(selectedEditVisitMachineId) ||
                          isCollectionDirty
                        }
                      >
                        {isSavingEditDate ? "Guardando fecha..." : "Aplicar fecha"}
                      </Button>
                    </Box>
                  )}
                  {!isEditMode && draftDateEditState.showCancelChangeButton && (
                    <Alert className="collection-context-alert" severity="warning" sx={{ width: "100%" }}>
                      Cambiar la fecha reiniciará las máquinas ya cargadas en esta recaudación.
                    </Alert>
                  )}
                  {isEditMode && (
                    <MDBox sx={{ width: "100%" }}>
                      {isLoadingCollectionEditSafeBounds ? (
                        <MDTypography variant="caption" color="text">
                          Calculando rango histórico seguro...
                        </MDTypography>
                      ) : collectionEditSafeBounds ? (
                        <MDTypography variant="caption" color="text">
                          La fecha puede modificarse entre{" "}
                          {formatDateKeyDDMMYYYY(collectionEditSafeBounds.MinSafeEditDate)} y{" "}
                          {formatDateKeyDDMMYYYY(collectionEditSafeBounds.MaxSafeEditDate)} sin
                          cruzar cambios históricos.
                        </MDTypography>
                      ) : (
                        <MDTypography variant="caption" color="text">
                          La fecha puede corregirse solo si no cruza cambios históricos del local,
                          máquinas, porcentajes o contadores.
                        </MDTypography>
                      )}
                      {collectionEditSafeBounds?.MaxReason && (
                        <MDTypography variant="caption" color="text" display="block">
                          Límite posterior: {collectionEditSafeBounds.MaxReason}
                        </MDTypography>
                      )}
                      {collectionEditSafeBounds?.MinReason && (
                        <MDTypography variant="caption" color="text" display="block">
                          Límite anterior: {collectionEditSafeBounds.MinReason}
                        </MDTypography>
                      )}
                      <MDTypography variant="caption" color="text" display="block">
                        Si necesitás cambiarla a una fecha que cruza esos cambios, la recaudación
                        debe rehacerse en el contexto correcto.
                      </MDTypography>
                    </MDBox>
                  )}
                  {isEditMode && collectionEditSafeBoundsError && (
                    <Alert className="collection-context-alert" severity="warning" sx={{ width: "100%" }}>
                      {collectionEditSafeBoundsError} El backend seguirá validando la fecha al
                      guardar.
                    </Alert>
                  )}
                  {isEditMode &&
                    collectionEditSafeBounds?.OriginalDateOutsideComputedRange && (
                      <Alert className="collection-context-alert" severity="warning" sx={{ width: "100%" }}>
                        La fecha actual quedó fuera del rango histórico calculado. Conservá la
                        fecha original o revisá el contexto antes de guardar.
                      </Alert>
                    )}
                  {isEditMode && historicalEditDateError && (
                    <Alert className="collection-context-alert" severity="error" sx={{ width: "100%" }}>
                      {historicalEditDateError}
                    </Alert>
                  )}
                </MDBox>
                {!isEditMode && bootError && (
                  <Alert className="collection-context-alert" severity="error" sx={{ mb: 2 }}>
                    {bootError}
                  </Alert>
                )}
                {!isEditMode && draftInputBufferConflictCount > 0 && (
                  <Alert className="collection-context-alert" severity="warning" sx={{ mb: 2 }}>
                    Hay cambios locales que no pudieron recuperarse automáticamente.
                  </Alert>
                )}
                {isEditMode && editInputBufferConflictCount > 0 && (
                  <Alert className="collection-context-alert" severity="warning" sx={{ mb: 2 }}>
                    Hay cambios locales que no pudieron recuperarse automáticamente.
                  </Alert>
                )}
                {actionError && !selectedDraftLocalMachineId && !selectedEditVisitMachineId && (
                  <Alert className="collection-context-alert" severity="error" onClose={() => setActionError("")} sx={{ mb: 2 }}>
                    {actionError}
                  </Alert>
                )}
                {isEditMode && isCollectionDirty && (
                  <MDBox
                    className="collection-context-surface"
                    mb={2}
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    sx={{
                      border: "1px solid",
                      borderColor: "warning.main",
                      backgroundColor: "warning.main",
                      opacity: 0.12,
                    }}
                  >
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
                      La fecha quedó bloqueada porque ya comenzaste a cargar la recaudación.
                    </MDTypography>
                    {isEditMode && (
                      <Button size="small" variant="outlined" color="warning" onClick={handleResetCollectionDraft}>
                        Reiniciar carga
                      </Button>
                    )}
                  </MDBox>
                )}
                <MDTypography className="collection-context-text" variant="caption" color="text" sx={{ display: "block", mb: 2 }}>
                  {lastRelevantVisitDate
                    ? `Recaudación anterior: ${formatDateDDMMYYYY(lastRelevantVisitDate)}`
                    : "No existen recaudaciones anteriores"}
                </MDTypography>

                {!isEditMode ? (
                  <MDBox>
                    {selectedDraftLocalMachineId ? (
                      <MDBox>
                        <Box className="individual-editor-toolbar" sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 2 }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            sx={secondaryOutlinedButtonSx}
                            disabled={saving || isSavingDraftDate || Boolean(draftAction)}
                            onClick={() => {
                              if (savingRef.current || draftDateSavingRef.current || draftActionRef.current) return;
                              setSelectedDraftLocalMachineId(null);
                            }}
                          >
                            Volver
                          </Button>
                          <Box className="individual-editor-save-actions" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={saving || isSavingDraftDate || Boolean(draftAction)}
                              onClick={() => saveSelectedDraftMachine({ goToNextPending: false })}
                            >
                              {saving ? "Guardando..." : "Guardar"}
                            </Button>
                            <Button
                              variant="contained"
                              color="info"
                              disabled={saving || isSavingDraftDate || Boolean(draftAction)}
                              onClick={() => saveSelectedDraftMachine({ goToNextPending: true })}
                            >
                              {saving ? "Guardando..." : "Guardar y siguiente"}
                            </Button>
                          </Box>
                        </Box>
                        {actionError && (
                          <Alert severity="error" onClose={() => setActionError("")} sx={{ mb: 2 }}>
                            {actionError}
                          </Alert>
                        )}
                        <TypeBlock
                          type={selectedDraftMachineType}
                          header={
                            <TypeChipsHeader
                              type={selectedDraftMachineType}
                              typeLabel={machineTypeLabel(selectedDraftMachineType, 1)}
                              rightChip={getDraftMachineStatusLabel(selectedDraftMachine)}
                            />
                          }
                        >
                          <MachinesTableGroup
                            machinesArray={selectedDraftCalculatedMachines}
                            visitMachineDetails={visitMachineDetails}
                            userInputs={userInputs}
                            settledInputFields={settledInputFields}
                            handleInputChange={handleInputChange}
                            handleToggleReset={handleToggleReset}
                            wasCollectedByLM={wasCollectedByLM}
                            handleToggleWasCollected={handleWasCollectedChange}
                            counterVerificationContextsByLocalMachineId={counterVerificationContextsByLocalMachineId}
                            isLoading={isLoading || isLoadingDraftProgress}
                            compact={compact}
                            nf={nfDyn}
                            moneyFmt={moneyDyn}
                            lastGlobalVisitId={lastRelevantVisitId}
                            columnTitles={
                              selectedDraftMachineType === "Tragamoneda"
                                ? { In: "Entrada", Out: "Salida" }
                                : selectedDraftMachineType === "Peluchera"
                                ? { In: "Entrada", Out: "Salida" }
                                : { In: "Entrada" }
                            }
                            hideTotal={hasRent(selectedDraftMachine)}
                            individualEditorLayout
                          />
                        </TypeBlock>
                      </MDBox>
                    ) : (
                      <MDBox className="collection-general-list">
                        {isLoadingDraftProgress && (
                          <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 1 }}>
                            Cargando progreso...
                          </MDTypography>
                        )}
                        {renderDraftMachineGroups(draftMachineGroups.notRentedEntries)}
                        {draftMachineGroups.totalRented > 0 && (
                          <MDBox mt={2}>
                            <MDTypography variant="button" fontWeight="medium" display="block" sx={{ mb: 1 }}>
                              {rentedGroupTitle(draftMachineGroups.totalRented)}
                            </MDTypography>
                            {renderDraftMachineGroups(draftMachineGroups.rentedEntries, { rented: true })}
                          </MDBox>
                        )}
                        {draftProgressAndActionsBlock}
                      </MDBox>
                    )}

                    <AppActionDialog
                      open={openCancelDraftDialog}
                      onClose={() => {
                        if (!saving && !isSavingDraftDate && !draftAction) setOpenCancelDraftDialog(false);
                      }}
                      onConfirm={cancelCurrentDraft}
                      title="Cancelar recaudación"
                      description="Al cancelar, se eliminarán los datos de la recaudación."
                      cancelLabel="Volver"
                      confirmLabel={draftAction === "cancel" ? "Cancelando..." : "Cancelar recaudación"}
                      confirmColor="error"
                      busy={saving || isSavingDraftDate || Boolean(draftAction)}
                    />
                    <AppActionDialog
                      open={openDraftDateResetDialog}
                      onClose={() => {
                        if (!isSavingDraftDate) setOpenDraftDateResetDialog(false);
                      }}
                      onConfirm={() => applyDraftDateChange({ resetPersistedMachines: true })}
                      title="Confirmar cambio de fecha"
                      description="Cambiar la fecha reiniciará las máquinas ya cargadas en esta recaudación. Se eliminarán los datos ingresados y deberán cargarse nuevamente para la nueva fecha."
                      confirmLabel={isSavingDraftDate ? "Aplicando..." : "Confirmar cambio de fecha"}
                      confirmColor="error"
                      busy={isSavingDraftDate}
                    />
                  </MDBox>
                ) : (
                  <MDBox>
                    {editDateState.changed && (
                      <Alert className="collection-context-alert" severity="info" sx={{ mb: 2 }}>
                        Guardá o restablecé el cambio de fecha antes de corregir una máquina.
                      </Alert>
                    )}

                    {selectedEditVisitMachineId ? (
                      <MDBox>
                        <Box className="individual-editor-toolbar" sx={{ display: "flex", justifyContent: "space-between", gap: 1, flexWrap: "wrap", mb: 2 }}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            sx={secondaryOutlinedButtonSx}
                            disabled={saving || isSavingEditDate}
                            onClick={closeSelectedCompletedEditMachine}
                          >
                            Volver
                          </Button>
                          <Box className="individual-editor-save-actions" sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              variant="outlined"
                              color="error"
                              sx={dangerOutlinedButtonSx}
                              disabled={
                                saving ||
                                isSavingEditDate ||
                                !editInputDirtyMachineIdSet.has(
                                  Number(selectedEditMachine?.LocalMachineId ?? selectedEditMachine?.Id)
                                )
                              }
                              onClick={() => setOpenDiscardEditMachineDialog(true)}
                            >
                              Descartar cambios
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={
                                saving ||
                                isSavingEditDate ||
                                !editInputDirtyMachineIdSet.has(
                                  Number(selectedEditMachine?.LocalMachineId ?? selectedEditMachine?.Id)
                                )
                              }
                              onClick={saveSelectedCompletedEditMachine}
                            >
                              {saving ? "Guardando..." : "Guardar"}
                            </Button>
                          </Box>
                        </Box>
                        {actionError && (
                          <Alert severity="error" onClose={() => setActionError("")} sx={{ mb: 2 }}>
                            {actionError}
                          </Alert>
                        )}
                        <TypeBlock
                          type={selectedEditMachineType}
                          header={
                            <TypeChipsHeader
                              type={selectedEditMachineType}
                              typeLabel={machineTypeLabel(selectedEditMachineType, 1)}
                              rightChip={getCompletedEditMachineStatusLabel(selectedEditMachine)}
                            />
                          }
                        >
                          <MachinesTableGroup
                            machinesArray={selectedEditCalculatedMachines}
                            visitMachineDetails={visitMachineDetails}
                            userInputs={userInputs}
                            settledInputFields={settledInputFields}
                            handleInputChange={handleInputChange}
                            handleToggleReset={handleToggleReset}
                            wasCollectedByLM={wasCollectedByLM}
                            handleToggleWasCollected={handleWasCollectedChange}
                            counterVerificationContextsByLocalMachineId={counterVerificationContextsByLocalMachineId}
                            isLoading={isLoading}
                            compact={compact}
                            nf={nfDyn}
                            moneyFmt={moneyDyn}
                            lastGlobalVisitId={lastRelevantVisitId}
                            columnTitles={
                              selectedEditMachineType === "Tragamoneda"
                                ? { In: "Entrada", Out: "Salida" }
                                : selectedEditMachineType === "Peluchera"
                                ? { In: "Entrada", Out: "Salida" }
                                : { In: "Entrada" }
                            }
                            hideTotal={hasRent(selectedEditMachine)}
                            individualEditorLayout
                          />
                        </TypeBlock>
                      </MDBox>
                    ) : (
                      <MDBox className="collection-general-list">
                        {renderCompletedEditMachineGroups(completedEditMachineGroups.notRentedEntries)}
                        {completedEditMachineGroups.totalRented > 0 && (
                          <MDBox mt={2}>
                            <MDTypography variant="button" fontWeight="medium" display="block" sx={{ mb: 1 }}>
                              {rentedGroupTitle(completedEditMachineGroups.totalRented)}
                            </MDTypography>
                            {renderCompletedEditMachineGroups(completedEditMachineGroups.rentedEntries, { rented: true })}
                          </MDBox>
                        )}
                        <MDBox
                          className="collection-totals-block"
                          mt={2}
                          mb={2}
                          p={2}
                          borderRadius="md"
                          sx={{
                            border: "1px solid",
                            borderColor: "grey.300",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            flexWrap: "wrap",
                          }}
                        >
                          <MDBox>
                            <MDTypography variant="button" fontWeight="medium" display="block">
                              Total: {moneyDyn.format(editTotals?.TotalAmount ?? 0)}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block">
                              Cliente: {moneyDyn.format(editTotals?.TotalClientAmount ?? 0)} · Empresa:{" "}
                              {moneyDyn.format(editTotals?.TotalOwnerAmount ?? 0)}
                            </MDTypography>
                          </MDBox>
                          <Box
                            className="collection-edit-footer-actions"
                            sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                          >
                            <Button
                              variant="outlined"
                              color="secondary"
                              sx={secondaryOutlinedButtonSx}
                              disabled={saving || isSavingEditDate}
                              onClick={handleExitCompletedEdit}
                            >
                              Salir de edición
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              disabled={saving || isSavingEditDate}
                              onClick={handleFinishCompletedEdit}
                            >
                              Finalizar edición
                            </Button>
                          </Box>
                        </MDBox>
                      </MDBox>
                    )}
                  </MDBox>
                )}
                <AppActionDialog
                  open={openDiscardEditMachineDialog}
                  onClose={() => {
                    if (!saving && !isSavingEditDate) setOpenDiscardEditMachineDialog(false);
                  }}
                  onConfirm={discardSelectedCompletedEditMachine}
                  title="Descartar cambios"
                  description="¿Querés descartar los cambios de esta máquina?"
                  cancelLabel="Seguir editando"
                  confirmLabel="Descartar cambios"
                  confirmColor="error"
                  busy={saving || isSavingEditDate}
                />
                <AppActionDialog
                  open={openPendingEditDialog}
                  onClose={() => {
                    if (!saving && !isSavingEditDate) setOpenPendingEditDialog(false);
                  }}
                  title="Cambios sin guardar"
                  description="Hay máquinas con cambios sin guardar. Guardalos o descartalos antes de finalizar"
                  busy={saving || isSavingEditDate}
                />
                <AppActionDialog
                  open={openIncompleteSlotDialog}
                  onClose={() => setOpenIncompleteSlotDialog(false)}
                  title="Datos incompletos"
                  description="Para guardar debe completar datos de entrada y salida."
                />
              </MDBox>

              {/* Barra de guardado reutilizable */}
              {false && isEditMode && (
                <SaveBar
                  onClick={() => saveOrEdit(isEditMode)}
                  saving={saving}
                  saveLabel="Actualizar recaudación"
                  savingLabel="Actualizando"
                />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <AppSnackbar
        key={editSuccessMessageKey}
        open={Boolean(editSuccessMessage)}
        onClose={handleCloseEditSuccess}
        severity="success"
        message={editSuccessMessage}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default VisitMachine;

















